import React, { useState, useEffect } from 'react';
import { db, storage } from '../firebaseConfig';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { useTranslation } from '../context/LanguageContext';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, query } from 'firebase/firestore';
import { validatePassword } from '../utils/validation';
import { MediaLightboxModal } from './MediaLightboxModal';
import { ExerciseFormBuilder } from './ExerciseFormBuilder';
import { InteractiveWorkoutBuilder } from './InteractiveWorkoutBuilder';
import { InteractivePlanBuilder } from './InteractivePlanBuilder';
/* ==========================================
   HELPER: DYNAMIC TERM TRANSLATOR (EN -> PL)
   ========================================== */
const translateTerm = (term, lang = 'pl') => {
  if (!term || typeof term !== 'string') return term || '-';
  if (lang !== 'pl') return term;

  const text = term.trim();
  const lower = text.toLowerCase();

  if (text.includes(',')) {
    return text.split(',').map(s => translateTerm(s, lang)).join(', ');
  }

  const dictionary = {
    'upper legs': 'Uda i Pośladki',
    'upper_legs': 'Uda i Pośladki',
    'lower legs': 'Łydki',
    'lower_legs': 'Łydki',
    'upper arms': 'Ramiona',
    'upper_arms': 'Ramiona',
    'lower arms': 'Przedramiona',
    'lower_arms': 'Przedramiona',
    'chest': 'Klatka piersiowa',
    'back': 'Plecy i Grzbiet',
    'shoulders': 'Barki',
    'waist': 'Brzuch / Pas',
    'waist / core': 'Brzuch / Pas',
    'cardio': 'Kardio',
    'neck': 'Szyja',
    'legs': 'Nogi',
    'body weight': 'Ciężar ciała',
    'bodyweight': 'Ciężar ciała',
    'dumbbell': 'Hantle',
    'dumbbells': 'Hantle',
    'barbell': 'Sztanga',
    'cable': 'Wyciąg',
    'leverage machine': 'Maszyna',
    'machine': 'Maszyna',
    'band': 'Guma oporowa',
    'smith machine': 'Suwnica Smitha',
    'kettlebell': 'Kettlebell',
    'kettlebells': 'Kettlebell',
    'weighted': 'Z dociążeniem',
    'stability ball': 'Piłka gimnastyczna',
    'ez barbell': 'Sztanga łamana',
    'ez_barbell': 'Sztanga łamana',
    'other': 'Inny',
    'quads': 'Czworogłowy uda',
    'quadriceps': 'Czworogłowy uda',
    'hamstrings': 'Kulszowo-goleniowe',
    'glutes': 'Pośladki',
    'gluteus maximus': 'Pośladkowy większy',
    'pectoralis major': 'Piersiowy większy',
    'latissimus dorsi': 'Najszerszy grzbietu',
    'deltoids': 'Naramienne (barki)',
    'deltoid': 'Naramienny',
    'biceps': 'Biceps (dwugłowy ramienia)',
    'biceps brachii': 'Biceps (dwugłowy ramienia)',
    'triceps': 'Triceps (trójgłowy ramienia)',
    'triceps brachii': 'Triceps (trójgłowy ramienia)',
    'abs': 'Prosty brzucha',
    'rectus abdominis': 'Prosty brzucha',
    'core': 'Mięśnie głębokie (core)',
    'calves': 'Brzuchaty łydki',
    'gastrocnemius': 'Brzuchaty łydki',
    'trapezius': 'Czworoboczny (kaptury)',
    'rhomboids': 'Równoległoboczne',
    'erector spinae': 'Prostownik grzbietu',
    'lower back': 'Dolny grzbiet',
    'upper back': 'Górny grzbiet',
    'obliques': 'Skośne brzucha',
    'forearms': 'Przedramiona',
    'upper chest': 'Góra klatki piersiowej',
    'lower chest': 'Dół klatki piersiowej',
    'long head': 'Głowa długa',
    'short head': 'Głowa krótka',
    'anterior deltoid': 'Przednia część naramiennego',
    'rear deltoid': 'Tylna część naramiennego',
    'posterior deltoid': 'Tylna część naramiennego',
    'lateral deltoid': 'Boczna część naramiennego',
    'brachialis': 'Ramienny',
    'hip flexors': 'Zginacze bioder'
  };

  if (dictionary[lower]) return dictionary[lower];

  for (const [key, translated] of Object.entries(dictionary)) {
    if (lower.includes(key)) {
      return lower.replace(key, translated);
    }
  }

  return text;
};

export default function SuperAdminPortal({ adminUser, logout }) {
  const { lang: locale, changeLanguage, setLang, t } = useTranslation();
  const [adminTab, setAdminTab] = useState('coaches'); // 'coaches' | 'auditor' | 'settings'

  // 1. Initialize state strictly as empty arrays for pure live Firestore data
  const [allUsers, setAllUsers] = useState([]);
  const [allWorkouts, setAllWorkouts] = useState([]);

  // 2. Create Coach Form States
  const [newCoachName, setNewCoachName] = useState("");
  const [newCoachEmail, setNewCoachEmail] = useState("");
  const [newCoachPassword, setNewCoachPassword] = useState("");
  const [newCoachLanguage, setNewCoachLanguage] = useState("pl");
  const [isProvisioning, setIsProvisioning] = useState(false);
  const [brandCustom, setBrandCustom] = useState(false);
  const [brandColor, setBrandColor] = useState("#6366f1"); // Default Indigo
  const [brandLogo, setBrandLogo] = useState("");
  const [editingCoachBranding, setEditingCoachBranding] = useState(null); // Active coach being edited

  // 3. Trainee Creator & Assignment Form States
  const [newTraineeName, setNewTraineeName] = useState("");
  const [newTraineeEmail, setNewTraineeEmail] = useState("");
  const [newTraineePassword, setNewTraineePassword] = useState("");
  const [newTraineeLanguage, setNewTraineeLanguage] = useState("en"); // "en" | "pl"
  const [newTraineeGender, setNewTraineeGender] = useState("male"); // "male" | "female"
  const [assignedTrainerId, setAssignedTrainerId] = useState(""); // Selected Coach ID
  const [isProvisioningTrainee, setIsProvisioningTrainee] = useState(false);
  const [isTraineeFormExpanded, setIsTraineeFormExpanded] = useState(false);

  // 4. Search & Detail State Filters
  const [coachSearchQuery, setCoachSearchQuery] = useState("");
  const [traineeSearchQuery, setTraineeSearchQuery] = useState("");
  const [selectedTraineeWorkouts, setSelectedTraineeWorkouts] = useState([]);
  const [auditedTraineeName, setAuditedTraineeName] = useState("");
  const [passwordModalUser, setPasswordModalUser] = useState(null);
  const [newPasswordInput, setNewPasswordInput] = useState("");
  const [showCoachPassword, setShowCoachPassword] = useState(false);
  const [showTraineePassword, setShowTraineePassword] = useState(false);
  const [showModalPassword, setShowModalPassword] = useState(false);

  // 5. Global Library States
  const [globalSubTab, setGlobalSubTab] = useState('exercises'); // 'exercises' | 'routines' | 'plans'
  const [allExercises, setAllExercises] = useState([]);
  const [allRoutines, setAllRoutines] = useState([]);
  const [allPlans, setAllPlans] = useState([]);

  // Form state for Global Exercise creation (reflecting exercises.schema.json with ENG & PL instructions)
  const [globalExLangTab, setGlobalExLangTab] = useState('pl'); // 'pl' | 'en'
  const [globalExNamePL, setGlobalExNamePL] = useState("");
  const [globalExNameEN, setGlobalExNameEN] = useState("");
  const [globalExBodyPart, setGlobalExBodyPart] = useState("chest");
  const [globalExTarget, setGlobalExTarget] = useState("");
  const [globalExMuscleGroup, setGlobalExMuscleGroup] = useState("");
  const [globalExSubPart, setGlobalExSubPart] = useState("");
  const [globalExSecondaryMuscles, setGlobalExSecondaryMuscles] = useState("");
  const [globalExEquipment, setGlobalExEquipment] = useState("body weight");
  const [globalExInstructionsPL, setGlobalExInstructionsPL] = useState("");
  const [globalExInstructionsEN, setGlobalExInstructionsEN] = useState("");
  const [globalExMediaId, setGlobalExMediaId] = useState("");
  const [globalExAttribution, setGlobalExAttribution] = useState("Gym visual");
  const [globalExHintsPL, setGlobalExHintsPL] = useState("");
  const [globalExHintsEN, setGlobalExHintsEN] = useState("");
  const [globalExImageFile, setGlobalExImageFile] = useState(null);
  const [globalExImageUrlInput, setGlobalExImageUrlInput] = useState("");
  const [globalExYoutubeUrl, setGlobalExYoutubeUrl] = useState("");
  const [isUploadingImage, setIsUploadingImage] = useState(false);

  // Editing state for Global Exercises in Super Admin Portal
  const [editingGlobalExId, setEditingGlobalExId] = useState(null);
  const [editingGlobalExNamePL, setEditingGlobalExNamePL] = useState("");
  const [editingGlobalExNameEN, setEditingGlobalExNameEN] = useState("");
  const [editingGlobalExBodyPart, setEditingGlobalExBodyPart] = useState("Legs");
  const [editingGlobalExSubPart, setEditingGlobalExSubPart] = useState("");
  const [editingGlobalExHintsPL, setEditingGlobalExHintsPL] = useState("");
  const [editingGlobalExHintsEN, setEditingGlobalExHintsEN] = useState("");
  const [editingGlobalExImageFile, setEditingGlobalExImageFile] = useState(null);
  const [editingGlobalExImageUrlInput, setEditingGlobalExImageUrlInput] = useState("");
  const [editingGlobalExYoutubeUrl, setEditingGlobalExYoutubeUrl] = useState("");
  const [editingGlobalExLangTab, setEditingGlobalExLangTab] = useState('pl');
  const [isUpdatingGlobalExImage, setIsUpdatingGlobalExImage] = useState(false);
  const [enlargedGlobalImage, setEnlargedGlobalImage] = useState(null);

  // Google Sheets Direct Import State for Global Exercises
  const [showGoogleSheetImportCard, setShowGoogleSheetImportCard] = useState(false);
  const [googleSheetUrlInput, setGoogleSheetUrlInput] = useState("https://docs.google.com/spreadsheets/d/1z2lNb9BnmV6zyDJ4D3ExOZW4HEyC4JR_qhrD_6lN_mQ/edit?usp=sharing");
  const [isImportingGoogleSheet, setIsImportingGoogleSheet] = useState(false);
  const [googleSheetImportReport, setGoogleSheetImportReport] = useState(null);

  // Search, Muscle & Equipment Filter for Global Exercise Library
  const [globalExSearchQuery, setGlobalExSearchQuery] = useState("");
  const [globalExFilterMuscle, setGlobalExFilterMuscle] = useState("ALL");
  const [globalExFilterEquipment, setGlobalExFilterEquipment] = useState("ALL");

  // Form state for Global Routine creation
  const [globalRtTitle, setGlobalRtTitle] = useState("");
  const [globalRtTag, setGlobalRtTag] = useState("#FBW");
  const [globalRtCustomTag, setGlobalRtCustomTag] = useState("");
  const [globalRtExercises, setGlobalRtExercises] = useState([]);
  const [globalRtSearchQuery, setGlobalRtSearchQuery] = useState("");
  const [globalRtBodyPartFilter, setGlobalRtBodyPartFilter] = useState("ALL");

  // Form state for Global Plan creation
  const [globalPlanTitle, setGlobalPlanTitle] = useState("");
  const [globalPlanNotes, setGlobalPlanNotes] = useState("");
  const [globalPlanRoutines, setGlobalPlanRoutines] = useState([{ dayNumber: 1, dayLabel: "Dzień 1", routineId: "", routineTitle: "" }]);

  useEffect(() => {
    const unsubEx = onSnapshot(query(collection(db, "exercises")), (snapshot) => {
      setAllExercises(snapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() })));
    });
    const unsubRt = onSnapshot(query(collection(db, "routines")), (snapshot) => {
      setAllRoutines(snapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() })));
    });
    const unsubPl = onSnapshot(query(collection(db, "plans")), (snapshot) => {
      setAllPlans(snapshot.docs.map(d => ({ id: d.id, docId: d.id, ...d.data() })));
    });
    return () => {
      unsubEx();
      unsubRt();
      unsubPl();
    };
  }, []);

  const parseCSV = (csvText) => {
    const lines = [];
    let currentLine = [];
    let currentField = '';
    let inQuotes = false;

    const delimiter = csvText.includes(';') ? ';' : ',';

    for (let i = 0; i < csvText.length; i++) {
      const char = csvText[i];
      const nextChar = csvText[i + 1];

      if (char === '"') {
        if (inQuotes && nextChar === '"') {
          currentField += '"';
          i++;
        } else {
          inQuotes = !inQuotes;
        }
      } else if (char === delimiter && !inQuotes) {
        currentLine.push(currentField.trim());
        currentField = '';
      } else if ((char === '\r' || char === '\n') && !inQuotes) {
        if (char === '\r' && nextChar === '\n') {
          i++;
        }
        currentLine.push(currentField.trim());
        if (currentLine.some(f => f !== '')) {
          lines.push(currentLine);
        }
        currentLine = [];
        currentField = '';
      } else {
        currentField += char;
      }
    }

    if (currentField || currentLine.length > 0) {
      currentLine.push(currentField.trim());
      if (currentLine.some(f => f !== '')) {
        lines.push(currentLine);
      }
    }

    return lines;
  };

  const downloadSampleCSV = () => {
    const csvContent = "Exercise Name ENG,Exercise Name PL,Body Part,Sub Part,Technical Hints ENG,Technical Hints PL,Picture/GIF Link,YouTube URL\n" +
      "\"Barbell Squat\",\"Przysiad ze Sztangą na Plecach\",\"Legs\",\"Quadriceps\",\"Keep feet shoulder-width, chest up, knees tracking toes.\",\"Stopy na szerokość barków, klatka w górę, kolana prowadzone z palcami.\",\"https://images.unsplash.com/photo-1574680096145-d05b474e2155\",\"https://www.youtube.com/watch?v=ultWZbUMPL8\"\n" +
      "\"Bench Press\",\"Wyciskanie Sztangi Poziomo\",\"Chest\",\"Upper Chest\",\"Retract scapula, arch slightly, drive feet into ground.\",\"Retrakcja łopatek, lekki mostek, wbij stopy w podłoże.\",\"https://images.unsplash.com/photo-1517838277536-f5f99be501cd\",\"https://www.youtube.com/watch?v=rT7DgCr-3pg\"\n" +
      "\"Pull-Ups\",\"Podciąganie na Drążku\",\"Back\",\"Lats\",\"Full elbow extension at bottom, pull chest to bar.\",\"Pełen wyprost w łokciach na dole, dociągnij klatkę do drążka.\",\"\",\"https://www.youtube.com/watch?v=eGo4IYlbE5g\"\n";
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute("download", "GymGhost_Standard_Global_Exercises_Sample.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSyncGoogleSheet = async (customUrl) => {
    const urlToUse = customUrl || googleSheetUrlInput;
    if (!urlToUse || !urlToUse.trim()) {
      alert(locale === 'pl' ? "Proszę podać link do Google Sheets." : "Please enter a Google Sheets URL.");
      return;
    }

    setIsImportingGoogleSheet(true);
    setGoogleSheetImportReport(null);

    try {
      const match = urlToUse.match(/\/spreadsheets\/d\/([a-zA-Z0-9-_]+)/);
      if (!match || !match[1]) {
        alert(locale === 'pl' 
          ? "Nieprawidłowy format linku do Google Sheets. Link powinien wyglądać tak: https://docs.google.com/spreadsheets/d/..." 
          : "Invalid Google Sheets URL format. URL should look like: https://docs.google.com/spreadsheets/d/...");
        setIsImportingGoogleSheet(false);
        return;
      }

      const sheetId = match[1];
      const exportUrls = [
        `https://docs.google.com/spreadsheets/d/${sheetId}/export?format=csv`,
        `https://docs.google.com/spreadsheets/d/${sheetId}/gviz/tq?tqx=out:csv`,
        `https://docs.google.com/spreadsheets/d/${sheetId}/pub?output=csv`
      ];

      let csvText = "";

      for (const exportUrl of exportUrls) {
        try {
          const res = await fetch(exportUrl);
          if (res.ok) {
            const text = await res.text();
            if (text && !text.includes("<!DOCTYPE html") && !text.includes("document-root loading")) {
              csvText = text;
              break;
            }
          }
        } catch (err) {
          console.warn("Fetch exportUrl attempt error:", err.message);
        }
      }

      if (!csvText) {
        alert(locale === 'pl' 
          ? "⚠️ Nie można pobrać danych z Google Sheets. Upewnij się, że w ustawieniach udostępniania arkusza zaznaczono: 'Każda osoba mająca link może wyświetlać' (File -> Share -> Anyone with the link can view)." 
          : "⚠️ Unable to fetch data from Google Sheets. Make sure sheet sharing is set to 'Anyone with the link can view'.");
        setIsImportingGoogleSheet(false);
        return;
      }

      const rows = parseCSV(csvText);

      if (rows.length === 0) {
        alert(locale === 'pl' ? "Arkusz Google Jest pusty!" : "Google Sheet is empty!");
        setIsImportingGoogleSheet(false);
        return;
      }

      const firstRow = rows[0].map(c => c.toLowerCase().trim());
      let headerMap = {
        nameENG: -1,
        namePL: -1,
        bodyPart: -1,
        subPart: -1,
        hintsENG: -1,
        hintsPL: -1,
        imageLink: -1,
        youtubeUrl: -1
      };

      firstRow.forEach((col, idx) => {
        if (col.includes('eng') && col.includes('name')) headerMap.nameENG = idx;
        else if (col.includes('en') && col.includes('name')) headerMap.nameENG = idx;
        else if (col.includes('pl') && col.includes('name')) headerMap.namePL = idx;
        else if (col.includes('sub') || col.includes('podpartia')) headerMap.subPart = idx;
        else if (col.includes('body') || col.includes('part') || col.includes('partia')) headerMap.bodyPart = idx;
        else if (col.includes('eng') && (col.includes('hint') || col.includes('wskaz'))) headerMap.hintsENG = idx;
        else if (col.includes('en') && (col.includes('hint') || col.includes('wskaz'))) headerMap.hintsENG = idx;
        else if (col.includes('pl') && (col.includes('hint') || col.includes('wskaz'))) headerMap.hintsPL = idx;
        else if (col.includes('picture') || col.includes('gif') || col.includes('image') || col.includes('zdjęcie')) headerMap.imageLink = idx;
        else if (col.includes('youtube') || col.includes('video')) headerMap.youtubeUrl = idx;
      });

      const hasHeader = headerMap.nameENG !== -1 || headerMap.namePL !== -1 || firstRow.some(r => r.includes('exercise') || r.includes('name'));
      const startIdx = hasHeader ? 1 : 0;

      if (headerMap.nameENG === -1) headerMap.nameENG = 0;
      if (headerMap.namePL === -1) headerMap.namePL = 1;
      if (headerMap.bodyPart === -1) headerMap.bodyPart = 2;
      if (headerMap.subPart === -1) headerMap.subPart = 3;
      if (headerMap.hintsENG === -1) headerMap.hintsENG = 4;
      if (headerMap.hintsPL === -1) headerMap.hintsPL = 5;
      if (headerMap.imageLink === -1) headerMap.imageLink = 6;
      if (headerMap.youtubeUrl === -1) headerMap.youtubeUrl = 7;

      const existingNamesSet = new Set(
        allExercises.flatMap(ex => [
          (ex.name || "").toLowerCase().trim(),
          (ex.namePL || "").toLowerCase().trim(),
          (ex.nameEN || "").toLowerCase().trim()
        ]).filter(Boolean)
      );

      let importedCount = 0;
      let duplicatesCount = 0;
      let skippedNames = [];

      for (let i = startIdx; i < rows.length; i++) {
        const row = rows[i];
        if (!row || row.length === 0 || row.every(c => !c)) continue;

        const nameENG = (row[headerMap.nameENG] || "").trim();
        const namePL = (row[headerMap.namePL] || "").trim();
        const bodyPartRaw = (row[headerMap.bodyPart] || "").trim();
        const subPart = headerMap.subPart !== -1 ? (row[headerMap.subPart] || "").trim() : "";
        const hintsENG = (row[headerMap.hintsENG] || "").trim();
        const hintsPL = (row[headerMap.hintsPL] || "").trim();
        const imageLink = (row[headerMap.imageLink] || "").trim();
        const youtubeUrl = (row[headerMap.youtubeUrl] || "").trim();

        const primaryName = nameENG || namePL;
        if (!primaryName) continue;

        const cleanENG = nameENG.toLowerCase();
        const cleanPL = namePL.toLowerCase();

        const isDuplicate = (cleanENG && existingNamesSet.has(cleanENG)) || (cleanPL && existingNamesSet.has(cleanPL));

        if (isDuplicate) {
          duplicatesCount++;
          skippedNames.push(primaryName);
          continue;
        }

        let bodyPart = "Legs";
        const bpLower = bodyPartRaw.toLowerCase();
        if (bpLower.includes('chest') || bpLower.includes('klat')) bodyPart = "Chest";
        else if (bpLower.includes('back') || bpLower.includes('plec')) bodyPart = "Back";
        else if (bpLower.includes('biceps') || bpLower.includes('ramiona')) bodyPart = "Biceps";
        else if (bpLower.includes('triceps')) bodyPart = "Triceps";
        else if (bpLower.includes('shoulder') || bpLower.includes('bark')) bodyPart = "Shoulders";
        else if (bpLower.includes('core') || bpLower.includes('brzuch') || bpLower.includes('abs')) bodyPart = "Core";
        else if (bpLower.includes('leg') || bpLower.includes('nog')) bodyPart = "Legs";
        else if (bodyPartRaw) bodyPart = bodyPartRaw;

        const exId = 'ex_global_gsheet_' + Date.now() + '_' + Math.floor(Math.random() * 1000);
        const primaryHints = hintsPL || hintsENG;

        const payload = {
          id: exId,
          docId: exId,
          name: primaryName,
          namePL: namePL || primaryName,
          nameEN: nameENG || primaryName,
          bodyPart: bodyPart,
          subPart: subPart,
          hints: primaryHints,
          notes: primaryHints,
          hintsPL: hintsPL || primaryHints,
          hintsEN: hintsENG || primaryHints,
          imageUrl: imageLink,
          imagePath: "",
          youtubeUrl: youtubeUrl,
          videoUrl: youtubeUrl,
          isGlobal: true,
          trainerId: "global",
          createdAt: Date.now()
        };

        await setDoc(doc(db, "exercises", exId), payload);
        if (cleanENG) existingNamesSet.add(cleanENG);
        if (cleanPL) existingNamesSet.add(cleanPL);
        importedCount++;
      }

      setGoogleSheetImportReport({
        imported: importedCount,
        duplicates: duplicatesCount,
        skippedNames: skippedNames
      });
    } catch (err) {
      console.error("Google Sheet Import error:", err);
      alert("Error importing Google Sheet: " + err.message);
    } finally {
      setIsImportingGoogleSheet(false);
    }
  };

  // Real-time listener for all system accounts from Firestore
  useEffect(() => {
    const q = query(collection(db, "users"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const docs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      const cleanDocs = docs.filter(d => {
        const email = (d.email || "").toLowerCase().trim();
        const id = (d.id || "").toLowerCase().trim();
        const isLegacyMock = email === 'client-demo@google.com' || id === 'client_123456';
        return !isLegacyMock;
      });
      const enhancedDocs = cleanDocs.map(d => {
        const email = (d.email || "").toLowerCase().trim();
        const name = (d.displayName || d.name || "").toLowerCase().trim();
        const role = (d.role || "").toLowerCase().trim();
        if ((email.includes('marek') || name.includes('marek') || name.includes('testowy')) && (role === 'trainer' || role === 'coach') && d.avatarUrl !== "/marek_avatar.jpg") {
          updateDoc(doc(db, "users", d.id || email), { avatarUrl: "/marek_avatar.jpg" }).catch(() => {});
          return { ...d, avatarUrl: "/marek_avatar.jpg" };
        }
        if ((email.includes('kinga') || name.includes('kinga') || (name.includes('testowa') && role !== 'trainer' && role !== 'coach')) && d.avatarUrl !== "/kinga_avatar.jpg") {
          updateDoc(doc(db, "users", d.id || email), { avatarUrl: "/kinga_avatar.jpg", photoURL: "/kinga_avatar.jpg" }).catch(() => {});
          return { ...d, avatarUrl: "/kinga_avatar.jpg", photoURL: "/kinga_avatar.jpg" };
        }
        return d;
      });
      setAllUsers(enhancedDocs);
    }, (err) => {
      console.warn("Firestore users listener error:", err.message);
    });
    return () => unsubscribe();
  }, []);

  // Real-time listener for all logged workouts across the entire SaaS platform
  useEffect(() => {
    const q = query(collection(db, "workouts"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setAllWorkouts(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.warn("Firestore workouts listener error:", err.message);
    });
    return () => unsubscribe();
  }, []);

  // Listen to account creation and deletion events across components for instant offline state sync
  useEffect(() => {
    const handleAccountDeleted = (e) => {
      const deletedId = e.detail?.id || e.detail;
      setAllUsers(prev => prev.filter(u => u.id !== deletedId && u.email !== e.detail?.email));
    };
    const handleAccountCreated = (e) => {
      if (e.detail) {
        setAllUsers(prev => [e.detail, ...prev.filter(u => u.id !== e.detail.id && u.email !== e.detail.email)]);
      }
    };
    window.addEventListener('trainee_deleted', handleAccountDeleted);
    window.addEventListener('coach_deleted', handleAccountDeleted);
    window.addEventListener('trainee_provisioned', handleAccountCreated);
    window.addEventListener('coach_provisioned', handleAccountCreated);
    return () => {
      window.removeEventListener('trainee_deleted', handleAccountDeleted);
      window.removeEventListener('coach_deleted', handleAccountDeleted);
      window.removeEventListener('trainee_provisioned', handleAccountCreated);
      window.removeEventListener('coach_provisioned', handleAccountCreated);
    };
  }, []);

  // NEW CLEAN CODES: Pure database array bindings matching all role aliases
  const isCoachAccount = (u) => {
    const r = (u?.role || "").toLowerCase().trim();
    return r === 'trainer' || r === 'coach';
  };

  const isTraineeAccount = (u) => {
    const r = (u?.role || "").toLowerCase().trim();
    return r !== 'admin' && !isCoachAccount(u);
  };

  const coaches = allUsers.filter(isCoachAccount);
  const trainees = allUsers.filter(isTraineeAccount);
  const workouts = allWorkouts;

  // Create Coach (Writes profile document directly to /users collection with preferred language and email verification)
  const handleCreateCoach = (e) => {
    e.preventDefault();
    if (!newCoachEmail || !newCoachName || !newCoachPassword) return;
    
    // SECURE VALIDATION CHECK
    const pwCheck = validatePassword(newCoachPassword);
    if (!pwCheck.isValid) {
      alert(locale === "pl" ? pwCheck.messagePL : pwCheck.messageEN);
      return;
    }
    const coachId = newCoachEmail.toLowerCase().trim();

    // Verify if email exists in system and allow admin to upgrade or update existing record
    const existingUser = allUsers.find(u => (u.email && u.email.toLowerCase().trim() === coachId) || (u.id && u.id.toLowerCase().trim() === coachId));
    if (existingUser && existingUser.role !== 'trainer') {
      const confirmUpgrade = window.confirm(locale === 'pl' 
        ? `⚠️ Adres e-mail "${coachId}" istnieje aktualnie w systemie z rolą ${existingUser.role.toUpperCase()}.\n\nCzy chcesz zaktualizować (awansować) to konto do roli TRENER?` 
        : `⚠️ The email "${coachId}" currently exists with role ${existingUser.role.toUpperCase()}.\n\nWould you like to upgrade this account to TRAINER?`);
      if (!confirmUpgrade) return;
    } else if (existingUser && existingUser.role === 'trainer') {
      const confirmUpdate = window.confirm(locale === 'pl'
        ? `Konto trenera "${coachId}" już istnieje. Czy chcesz zaktualizować jego dane profilowe?`
        : `Trainer account "${coachId}" already exists. Would you like to update their profile details?`);
      if (!confirmUpdate) return;
    }

    setIsProvisioning(true);
    try {
      const payload = {
        id: coachId,
        displayName: newCoachName.trim(),
        email: coachId,
        role: "trainer",
        status: "active",
        language: newCoachLanguage || "pl",
        brandCustom: brandCustom,
        brandColor: brandCustom ? brandColor : "#6366f1",
        brandLogo: brandCustom ? brandLogo.trim() : "",
        avatarUrl: (coachId.includes('wieslaw') ? "/wieslaw_avatar.jpg" : ((coachId.includes('marek') || newCoachName.toLowerCase().includes('marek') || newCoachName.toLowerCase().includes('testowy')) ? "/marek_avatar.jpg" : "")),
        createdAt: new Date().toISOString()
      };

      // Execute non-blocking Firestore write to guarantee zero UI delays or offline freezes
      setDoc(doc(db, "users", coachId), payload, { merge: true }).catch(err => console.warn("Offline note:", err.message));
      setAllUsers(prev => [payload, ...prev.filter(u => u.id !== coachId && u.email !== coachId)]);
      window.dispatchEvent(new CustomEvent('coach_provisioned', { detail: payload }));

      setNewCoachName("");
      setNewCoachEmail("");
      setNewCoachPassword("");
      setNewCoachLanguage("pl");
      setBrandCustom(false);
      setBrandColor("#6366f1");
      setBrandLogo("");
      alert(locale === 'pl'
        ? `🎉 Profil trenera utworzy: "${payload.displayName}" (${payload.email})!\n\nStatus AKTYWNY, język domyślny [${payload.language.toUpperCase()}], gotowy do logowania.`
        : `🎉 Coach Profile provisioned: "${payload.displayName}" (${payload.email})!\n\nStatus is set to ACTIVE with default language [${payload.language.toUpperCase()}] and ready for immediate dashboard login.`);
    } catch (err) {
      console.error("Error creating coach account:", err);
      alert("Failed to provision coach account. Check network / console.");
    } finally {
      setIsProvisioning(false);
    }
  };

  // Create Trainee & Dynamically Assign Coach (Includes smart email role verification)
  const handleCreateTrainee = (e) => {
    e.preventDefault();
    if (!newTraineeEmail || !newTraineeName || !newTraineePassword || !assignedTrainerId) {
      alert(locale === 'pl' 
        ? "Proszę wypełnić wszystkie pola: imię, e-mail, hasło oraz wybrać przypisanego trenera." 
        : "Please enter trainee name, email, temp password, and select an assigned coach.");
      return;
    }

    // SECURE VALIDATION CHECK
    const pwCheck = validatePassword(newTraineePassword);
    if (!pwCheck.isValid) {
      alert(locale === "pl" ? pwCheck.messagePL : pwCheck.messageEN);
      return;
    }
    const traineeId = newTraineeEmail.toLowerCase().trim();

    // Verify if email exists and prevent downgrading trainers/admins while allowing re-assignment of athletes
    const existingUser = allUsers.find(u => (u.email && u.email.toLowerCase().trim() === traineeId) || (u.id && u.id.toLowerCase().trim() === traineeId));
    if (existingUser && (existingUser.role === 'trainer' || existingUser.role === 'admin')) {
      alert(locale === 'pl' 
        ? `⚠️ Odrzucono: Adres e-mail "${traineeId}" należy do konta o wysokich uprawnieniach (${existingUser.role.toUpperCase()}). Nie można pomyślnie zamienić na zawodnika.` 
        : `⚠️ Rejected: The email "${traineeId}" belongs to a ${existingUser.role.toUpperCase()} account and cannot be assigned as a trainee.`);
      return;
    } else if (existingUser && (existingUser.role === 'client' || existingUser.role === 'trainee')) {
      const confirmReassign = window.confirm(locale === 'pl'
        ? `Zawodnik o e-mailu "${traineeId}" już istnieje w bazie. Czy chcesz zaktualizować jego przypisanego trenera?`
        : `Athlete "${traineeId}" already exists. Would you like to update their assigned coach?`);
      if (!confirmReassign) return;
    }

    setIsProvisioningTrainee(true);
    try {
      const payload = {
        id: traineeId,
        displayName: newTraineeName.trim(),
        name: newTraineeName.trim(),
        email: traineeId,
        role: "client",
        trainerId: assignedTrainerId,
        status: "active",
        language: newTraineeLanguage || "en",
        gender: newTraineeGender || "male",
        avatarUrl: ((traineeId.includes('kinga') || newTraineeName.toLowerCase().includes('kinga') || newTraineeName.toLowerCase().includes('testowa')) ? "/kinga_avatar.jpg" : ((traineeId.includes('monika') || newTraineeName.toLowerCase().includes('monika')) ? "/monika_avatar.jpg?v=1" : "")),
        photoURL: ((traineeId.includes('kinga') || newTraineeName.toLowerCase().includes('kinga') || newTraineeName.toLowerCase().includes('testowa')) ? "/kinga_avatar.jpg" : ((traineeId.includes('monika') || newTraineeName.toLowerCase().includes('monika')) ? "/monika_avatar.jpg?v=1" : "")),
        createdAt: new Date().toISOString()
      };

      setDoc(doc(db, "users", traineeId), payload, { merge: true }).catch(err => console.warn("Offline note:", err.message));
      setAllUsers(prev => [payload, ...prev.filter(u => u.id !== traineeId && u.email !== traineeId)]);
      window.dispatchEvent(new CustomEvent('trainee_provisioned', { detail: payload }));

      setNewTraineeName("");
      setNewTraineeEmail("");
      setNewTraineePassword("");
      setNewTraineeLanguage("en");
      setNewTraineeGender("male");
      setAssignedTrainerId("");
      setIsTraineeFormExpanded(false);
      alert(locale === 'pl'
        ? `🎉 Profil zawodnika utworzony: "${payload.displayName}" (${payload.email})!\n\nPłeć: [${payload.gender === 'male' ? '👨 Mężczyzna' : '👩 Kobieta'}], zorganizowano dla trenera ID: [${payload.trainerId}].`
        : `🎉 Trainee Profile provisioned: "${payload.displayName}" (${payload.email})!\n\nGender: [${payload.gender === 'male' ? '👨 Male' : '👩 Female'}], paired to Coach ID: [${payload.trainerId}].`);
    } catch (err) {
      console.error("Error creating trainee account:", err);
      alert("Failed to provision trainee account. Check network / console.");
    } finally {
      setIsProvisioningTrainee(false);
    }
  };

  // Delete Individual Coach Profile immediately without blocking on network promises
  const handleDeleteCoach = (coachObj, e) => {
    e.stopPropagation();
    const name = coachObj.displayName || coachObj.name || coachObj.email;
    if (!window.confirm(locale === 'pl' 
      ? `Czy na pewno chcesz całkowicie usunąć konto trenera "${name}" (${coachObj.email}) z bazy danych?` 
      : `Are you sure you want to completely delete coach "${name}" (${coachObj.email}) from the database?`)) return;
    
    try {
      deleteDoc(doc(db, "users", coachObj.id)).catch(err => console.warn("Offline note:", err.message));
      setAllUsers(prev => prev.filter(u => u.id !== coachObj.id && u.email !== coachObj.email));
      window.dispatchEvent(new CustomEvent('coach_deleted', { detail: coachObj }));
      alert(locale === 'pl' ? `🗑️ Pomyślnie usunięto konto trenera: ${name}` : `🗑️ Successfully removed coach profile: ${name}`);
    } catch (err) {
      console.error("Failed to delete coach:", err);
    }
  };

  // Delete Individual Trainee Profile immediately without blocking on network promises
  const handleDeleteTrainee = (traineeObj, e) => {
    e.stopPropagation();
    const name = traineeObj.displayName || traineeObj.name || traineeObj.email;
    if (!window.confirm(locale === 'pl' 
      ? `Czy na pewno chcesz usunąć zawodnika "${name}" (${traineeObj.email}) z bazy danych?` 
      : `Are you sure you want to completely delete athlete "${name}" (${traineeObj.email}) from the database?`)) return;
    
    try {
      deleteDoc(doc(db, "users", traineeObj.id)).catch(err => console.warn("Offline note:", err.message));
      setAllUsers(prev => prev.filter(u => u.id !== traineeObj.id && u.email !== traineeObj.email));
      window.dispatchEvent(new CustomEvent('trainee_deleted', { detail: traineeObj }));
      
      if (auditedTraineeName === name) {
        setAuditedTraineeName("");
        setSelectedTraineeWorkouts([]);
      }
      alert(locale === 'pl' ? `🗑️ Pomyślnie usunięto zawodnika: ${name}` : `🗑️ Successfully removed trainee profile: ${name}`);
    } catch (err) {
      console.error("Failed to delete trainee:", err);
    }
  };

  const handleChangeUserPassword = (userObj) => {
    setPasswordModalUser(userObj);
    setNewPasswordInput("");
  };

  const handleExecuteChangePassword = async () => {
    if (!passwordModalUser) return;
    const pwCheck = validatePassword(newPasswordInput);
    if (!pwCheck.isValid) {
      alert(locale === "pl" ? pwCheck.messagePL : pwCheck.messageEN);
      return;
    }

    try {
      const targetEmail = (passwordModalUser.email || passwordModalUser.id || "").toLowerCase().trim();
      const targetId = (passwordModalUser.id || targetEmail).toLowerCase().trim();
      const name = passwordModalUser.displayName || passwordModalUser.name || targetEmail;
      const cleanPass = newPasswordInput.trim();

      const payload = { 
        password: cleanPass,
        tempPassword: cleanPass, 
        updatedPassword: cleanPass, 
        lastPasswordReset: new Date().toISOString() 
      };

      setDoc(doc(db, "users", targetId), payload, { merge: true }).catch(err => console.warn("Offline note:", err.message));
      if (targetEmail && targetEmail !== targetId) {
        setDoc(doc(db, "users", targetEmail), payload, { merge: true }).catch(() => {});
      }
      const alias = targetEmail.split('@')[0];
      if (alias && alias !== targetId && alias !== targetEmail) {
        setDoc(doc(db, "users", alias), payload, { merge: true }).catch(() => {});
      }
      
      setAllUsers(prev => prev.map(u => {
        if ((u.id || u.email)?.toLowerCase().trim() === targetEmail || (u.id || u.email)?.toLowerCase().trim() === targetId) {
          return { ...u, password: cleanPass, tempPassword: cleanPass, updatedPassword: cleanPass };
        }
        return u;
      }));

      window.dispatchEvent(new CustomEvent('password_updated', { detail: { email: targetEmail, newPassword: cleanPass } }));
      
      alert(locale === 'pl' 
        ? `🎉 Sukces! Hasło dla użytkownika "${name}" (${targetEmail}) zostało pomyślnie zmienione na: [${cleanPass}]. Stare hasło "Test123" zostało natychmiast unieważnione!`
        : `🎉 Success! Password for "${name}" (${targetEmail}) updated to: [${cleanPass}]. Old password "Test123" is no longer valid!`);
      setPasswordModalUser(null);
      setNewPasswordInput("");
    } catch (err) {
      console.error("Error updating password:", err);
      alert("Failed to update user password.");
    }
  };

  // Batch Purge All Existing Trainees instantaneously
  const handlePurgeAllTrainees = () => {
    if (!window.confirm(locale === 'pl' 
      ? `Czy na pewno chcesz usunąć wszystkie (${trainees.length}) profile zawodników z bazy danych? Operacji nie można cofnąć.`
      : `Are you sure you want to completely wipe all ${trainees.length} trainee profiles from your Firestore and local database? This cannot be undone.`)) return;
    
    try {
      trainees.forEach(t => {
        deleteDoc(doc(db, "users", t.id)).catch(() => {});
      });
      setAllUsers(prev => prev.filter(u => u.role === 'admin' || u.role === 'trainer'));
      setSelectedTraineeWorkouts([]);
      setAuditedTraineeName("");
      alert(locale === 'pl' ? "🧹 Pomyślnie usunięto wszystkie profile zawodników!" : "🧹 Successfully purged all trainee profiles from the database!");
    } catch (err) {
      console.error("Failed batch purge:", err);
    }
  };

  // Toggle Lock/Unlock Coach (Soft lock, retains data records & trainee pairings)
  const handleToggleLockCoach = (coachId, currentStatus) => {
    const newStatus = currentStatus === "locked" ? "active" : "locked";
    const targetName = coaches.find(c => c.id === coachId || c.email === coachId)?.displayName || coachId;
    
    if (!window.confirm(locale === 'pl'
      ? `Czy na pewno chcesz zmienić status trenera "${targetName}" na ${newStatus.toUpperCase()}?\n\n${newStatus === 'locked' ? '🔒 Blokada natychmiast odcina dostęp do Panelu Trenera.' : '🔓 Odblokowanie przywraca pełen dostęp do systemu.'}`
      : `Are you sure you want to set Coach "${targetName}" status to ${newStatus.toUpperCase()}?\n\n${newStatus === 'locked' ? '🔒 Locking immediately terminates their access to the Trainer Portal.' : '🔓 Unlocking immediately restores their access to the Trainer Portal and all client rosters.'}`)) return;

    try {
      updateDoc(doc(db, "users", coachId), { status: newStatus }).catch(err => console.warn("Offline note:", err.message));
      setAllUsers(prev => prev.map(u => (u.id === coachId || u.email === coachId) ? { ...u, status: newStatus } : u));
      window.dispatchEvent(new CustomEvent('coach_status_changed', { detail: { id: coachId, status: newStatus } }));
      alert(`✅ Status trenera "${targetName}" to teraz: ${newStatus.toUpperCase()}`);
    } catch (err) {
      console.error("Failed to toggle coach lock status:", err);
    }
  };

  // View Trainee Workout History against pure live database entries (newest date first)
  const handleAuditTraineeWorkouts = (trainee) => {
    const history = workouts.filter(w => w.userId === trainee.id || w.userId === trainee.email || w.clientEmail === trainee.email);
    const sortedHistory = [...history].sort((a, b) => {
      const dateA = (a.date || a.createdAt || "").toString();
      const dateB = (b.date || b.createdAt || "").toString();
      return dateB.localeCompare(dateA);
    });
    setSelectedTraineeWorkouts(sortedHistory);
    setAuditedTraineeName(trainee.displayName || trainee.name || trainee.email);
    setAdminTab('auditor');
  };

  return (
    <div className="min-h-screen bg-[#0a101d] text-slate-100 flex flex-col">
      {/* Sticky Top Admin Header */}
      <header className="w-full bg-slate-950 border-b border-slate-800 sticky top-0 z-50 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 md:px-6 flex flex-col md:flex-row md:items-center justify-between gap-4 py-3.5">
          
          {/* Left Column: Compact User Avatar & Info */}
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-rose-500/15 border-2 border-rose-500/40 flex items-center justify-center text-xl shadow-[0_0_15px_rgba(244,63,94,0.2)] shrink-0 animate-pulse">
              <span className="material-symbols-outlined text-2xl text-rose-400">shield_person</span>
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-black text-base text-slate-100 block tracking-wide">
                  {t('superAdmin.title', 'SaaS Super Admin')}
                </span>
                <span className="w-2 h-2 rounded-full bg-rose-400 shadow-[0_0_8px_#f43f5e]" />
              </div>
              <span className="text-[10px] text-rose-400 font-extrabold uppercase tracking-widest block mt-0.5">
                {t('superAdmin.subtitle', 'Platform Owner & Security Dashboard')}
              </span>
            </div>
          </div>

          {/* Center Column: Admin Panels Menu Nav */}
          <nav className="flex items-center gap-1.5 bg-slate-900/80 border border-slate-800 rounded-xl p-1.5 shrink-0 flex-wrap sm:flex-nowrap shadow-inner">
            <button
              onClick={() => setAdminTab('coaches')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                adminTab === 'coaches' ? 'bg-rose-600 text-white shadow-[0_4px_15px_rgba(244,63,94,0.35)] scale-[1.02]' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-950/60 bg-transparent border-0'
              }`}
            >
              <span className="material-symbols-outlined text-base leading-none">manage_accounts</span> <span>{t('superAdmin.tabCoaches', 'Coach Registry')} ({coaches.length})</span>
            </button>
            <button
              onClick={() => setAdminTab('auditor')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                adminTab === 'auditor' ? 'bg-rose-600 text-white shadow-[0_4px_15px_rgba(244,63,94,0.35)] scale-[1.02]' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-950/60 bg-transparent border-0'
              }`}
            >
              <span className="material-symbols-outlined text-base leading-none">groups</span> <span>{t('superAdmin.tabAuditor', 'Trainee & Workout Auditor')} ({trainees.length})</span>
            </button>
            <button
              onClick={() => setAdminTab('global_library')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                adminTab === 'global_library' ? 'bg-rose-600 text-white shadow-[0_4px_15px_rgba(244,63,94,0.35)] scale-[1.02]' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-950/60 bg-transparent border-0'
              }`}
            >
              <span className="material-symbols-outlined text-base leading-none">local_library</span> <span>{locale === 'pl' ? 'Globalna Biblioteka' : 'Global Library'}</span>
            </button>
            <button
              onClick={() => setAdminTab('settings')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-black transition-all cursor-pointer ${
                adminTab === 'settings' ? 'bg-rose-600 text-white shadow-[0_4px_15px_rgba(244,63,94,0.35)] scale-[1.02]' : 'text-slate-400 hover:text-slate-200 hover:bg-slate-950/60 bg-transparent border-0'
              }`}
            >
              <span className="material-symbols-outlined text-base leading-none">settings</span> <span>{t('superAdmin.tabSettings', 'Settings')}</span>
            </button>
          </nav>

          {/* Right Column: Standard Dark-Themed Sign Out Button Only */}
          <div className="flex items-center gap-3 shrink-0">
            <button 
              onClick={logout} 
              className="bg-slate-900 hover:bg-rose-500/15 text-slate-300 hover:text-rose-400 border border-slate-800 hover:border-rose-500/30 text-xs font-black px-5 py-2.5 rounded-xl transition cursor-pointer shadow active:scale-95 flex items-center gap-1.5"
            >
              <span className="material-symbols-outlined text-sm">logout</span> <span>{t('common.signOut', 'Sign Out')}</span>
            </button>
          </div>

        </div>
      </header>

      {/* Main Panel Content */}
      <main className="flex-1 w-full max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-8 pb-24">

        {/* TAB 1: COACH REGISTRY */}
        {adminTab === 'coaches' && (
          <div className="space-y-8 animate-fadeIn">
            <div className="border-b border-slate-800 pb-4 flex justify-between items-end flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-3xl text-rose-400">reorder</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                    {t('superAdmin.coachesTitle', 'Coach & Tenant Registry')}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                  {t('superAdmin.coachesDesc', 'Provision new training organization accounts with preferred localization, monitor active rosters per coach, and instantly freeze platform access.')}
                </p>
              </div>
            </div>

            {/* Create Coach Account Form */}
            <div className="bg-gradient-to-br from-slate-900/90 via-slate-900 to-slate-900 border-2 border-rose-500/40 rounded-2xl p-6 md:p-8 shadow-2xl space-y-5">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                <h3 className="text-sm md:text-base font-black text-white uppercase tracking-wider m-0 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base text-rose-400">add_circle</span>
                  <span>{t('superAdmin.provisionCoachHeader', 'Provision New Coach Account')}</span>
                </h3>
                <span className="text-[10px] font-black bg-rose-500/10 text-rose-400 border border-rose-500/30 px-3 py-1 rounded-full uppercase tracking-wider">
                  {t('superAdmin.adminBypass', 'Platform Admin bypass enabled')}
                </span>
              </div>
              
              <form onSubmit={handleCreateCoach} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                <div className="md:col-span-3">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                    {t('superAdmin.coachNameLabel', "Coach's Full Name")}
                  </label>
                  <input type="text" required value={newCoachName} onChange={e => setNewCoachName(e.target.value)} placeholder="np. Trener Mateusz" className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-bold rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-rose-400 shadow-inner" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                    {t('superAdmin.coachEmailLabel', "Coach's Email Address")}
                  </label>
                  <input type="email" required value={newCoachEmail} onChange={e => setNewCoachEmail(e.target.value)} placeholder="np. mateusz@google.com" className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-bold rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-rose-400 shadow-inner" />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                    {t('superAdmin.preferredLangLabel', 'Preferred Language')}
                  </label>
                  <select value={newCoachLanguage} onChange={e => setNewCoachLanguage(e.target.value)} className="w-full bg-slate-950 border border-slate-750 text-indigo-300 font-extrabold text-sm rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-rose-400 cursor-pointer shadow-inner">
                    <option value="pl">🇵🇱 Polski (PL)</option>
                    <option value="en">🇺🇸 English (EN)</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                    {t('superAdmin.tempPasswordLabel', 'Temp Password')}
                  </label>
                  <div className="relative flex items-center">
                    <input 
                      type={showCoachPassword ? "text" : "password"} 
                      required 
                      value={newCoachPassword} 
                      onChange={e => setNewCoachPassword(e.target.value)} 
                      placeholder="••••••••" 
                      className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-extrabold rounded-xl pl-3.5 pr-11 py-3.5 focus:outline-none focus:border-rose-400 shadow-inner" 
                    />
                    <button 
                      type="button" 
                      onClick={() => setShowCoachPassword(!showCoachPassword)} 
                      className="absolute right-3 text-slate-400 hover:text-white transition focus:outline-none flex items-center cursor-pointer p-1"
                      title={showCoachPassword ? (locale === 'pl' ? "Ukryj hasło" : "Hide password") : (locale === 'pl' ? "Pokaż hasło" : "Show password")}
                    >
                      <span className="material-symbols-outlined text-lg">{showCoachPassword ? "visibility_off" : "visibility"}</span>
                    </button>
                  </div>
                </div>

                {/* Second Row: Dedicated Password Requirements Bar & Provision Button */}
                <div className="md:col-span-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-slate-800/80 mt-1">
                  <div className="flex-1 bg-slate-950/60 border border-slate-800/80 rounded-xl p-3 flex flex-wrap items-center gap-2">
                    <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                      <span className="material-symbols-outlined text-xs text-slate-400">shield_lock</span>
                      {locale === 'pl' ? "Wymagania hasła:" : "Requirements:"}
                    </span>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${newCoachPassword.length >= 8 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                      Min. 8 Chars
                    </span>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[A-Z]/.test(newCoachPassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                      A-Z
                    </span>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[a-z]/.test(newCoachPassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                      a-z
                    </span>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[0-9]/.test(newCoachPassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                      0-9
                    </span>
                    <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[@$!%*?&._-]/.test(newCoachPassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                      Special Char
                    </span>
                  </div>

                  <div className="w-full sm:w-auto shrink-0">
                    <button type="submit" disabled={isProvisioning} className="w-full sm:w-[240px] bg-gradient-to-r from-rose-600 to-rose-500 hover:from-rose-500 hover:to-rose-400 text-white text-sm font-black py-3.5 px-6 rounded-xl transition shadow-xl shadow-rose-500/25 cursor-pointer active:scale-95 disabled:opacity-50 flex items-center justify-center gap-1.5">
                      {isProvisioning ? "..." : (
                        <>
                          <span className="material-symbols-outlined text-lg">bolt</span>
                          <span>{t('superAdmin.provisionBtn', 'Provision')} ➔</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>

                <div className="md:col-span-12 border-t border-slate-800 pt-4 mt-2 space-y-4">
                  <div className="flex items-center gap-3">
                    <input 
                      type="checkbox" 
                      id="brandCustom" 
                      checked={brandCustom} 
                      onChange={e => setBrandCustom(e.target.checked)} 
                      className="w-4 h-4 bg-slate-950 border-slate-800 rounded text-indigo-600 focus:ring-0 cursor-pointer"
                    />
                    <label htmlFor="brandCustom" className="text-xs font-bold text-slate-300 cursor-pointer inline-flex items-center gap-1.5">
                      <span className="material-symbols-outlined text-sm text-indigo-400">palette</span>
                      <span>{locale === 'pl' ? 'Włącz własny branding dla tego trenera (White-Labeling)' : 'Enable Custom Branding for this Trainer (White-Labeling)'}</span>
                    </label>
                  </div>

                  {brandCustom && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fadeIn bg-slate-950/50 p-4 rounded-xl border border-slate-800">
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                          {locale === 'pl' ? 'Logo (Tekst lub URL do obrazka PNG/SVG)' : 'Logo (Text or URL to PNG/SVG Image)'}
                        </label>
                        <input 
                          type="text" 
                          value={brandLogo} 
                          onChange={e => setBrandLogo(e.target.value)} 
                          placeholder={locale === 'pl' ? 'np. Team Piątek 🦅 lub https://mojadomena.pl/logo.png' : 'e.g., Team Piątek 🦅 or https://mydomain.com/logo.png'} 
                          className="w-full bg-slate-900 border border-slate-750 text-slate-100 text-xs font-bold rounded-xl px-3.5 py-3 focus:outline-none focus:border-rose-400" 
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1.5">
                          {locale === 'pl' ? 'Kolor Przewodni (Hex)' : 'Primary Brand Color (Hex)'}
                        </label>
                        <div className="flex gap-2">
                          <input 
                            type="color" 
                            value={brandColor} 
                            onChange={e => setBrandColor(e.target.value)} 
                            className="w-10 h-10 bg-transparent border-none cursor-pointer p-0 rounded-xl" 
                          />
                          <input 
                            type="text" 
                            value={brandColor} 
                            onChange={e => setBrandColor(e.target.value)} 
                            className="flex-1 bg-slate-900 border border-slate-750 text-slate-100 font-extrabold text-xs rounded-xl px-3 py-2 uppercase font-mono focus:outline-none focus:border-rose-400 text-center" 
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>

            {/* Coaches List & Trainee Stats Table */}
            <div className="bg-slate-900/60 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-xl space-y-6">
              <div className="flex justify-between items-center flex-wrap gap-4 border-b border-slate-800 pb-4">
                <h3 className="text-sm md:text-base font-black text-slate-300 uppercase tracking-wider m-0 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base text-sky-400">domain</span>
                  <span>{t('superAdmin.activeCoachesHeader', 'Active Coach Platforms Registered')} ({coaches.length})</span>
                </h3>
                <input type="text" placeholder={t('superAdmin.searchCoachesPlaceholder', 'Search Coaches by name or email...')} value={coachSearchQuery} onChange={e => setCoachSearchQuery(e.target.value)} className="bg-slate-950 border border-slate-700 text-white font-semibold text-xs md:text-sm rounded-xl px-4 py-2.5 focus:outline-none focus:border-rose-400 w-full sm:w-72 shadow-inner" />
              </div>

              {coaches.length === 0 ? (
                <div className="p-16 text-center bg-slate-950/40 border border-dashed border-slate-800 rounded-2xl text-slate-500 font-medium text-sm">
                  <span className="material-symbols-outlined text-4xl text-slate-500 block mb-2 mx-auto">domain</span>
                  {t('superAdmin.noCoachesFound', 'No coach platforms have been provisioned in Firestore yet. Use the form above to enroll your first trainer!')}
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {coaches
                    .filter(c => (c.displayName || "").toLowerCase().includes(coachSearchQuery.toLowerCase()) || (c.email || "").toLowerCase().includes(coachSearchQuery.toLowerCase()))
                    .map(coach => {
                      const coachTrainees = trainees.filter(t => t.trainerId === coach.id || t.trainerId === coach.email);
                      const activeCount = coachTrainees.filter(t => t.status !== 'archived').length;
                      const archivedCount = coachTrainees.filter(t => t.status === 'archived').length;
                      const isLocked = coach.status === 'locked';

                      return (
                        <div key={coach.id} className={`bg-slate-950/80 border-2 rounded-2xl p-6 flex flex-col justify-between shadow-2xl transition-all ${isLocked ? 'border-red-500/60 bg-red-950/10 shadow-[0_0_15px_rgba(239,68,68,0.15)]' : 'border-slate-800 hover:border-slate-700'}`}>
                          <div>
                            <div className="flex justify-between items-start mb-4 gap-3.5">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                {coach.avatarUrl || (coach.email || "").toLowerCase().includes('wieslaw') || (coach.displayName || "").toLowerCase().includes('wieslaw') || (coach.email || "").toLowerCase().includes('marek') || (coach.displayName || "").toLowerCase().includes('marek') || (coach.displayName || "").toLowerCase().includes('testowy') ? (
                                  <img src={coach.avatarUrl || (((coach.email || "").toLowerCase().includes('marek') || (coach.displayName || "").toLowerCase().includes('marek') || (coach.displayName || "").toLowerCase().includes('testowy')) ? "/marek_avatar.jpg" : "/wieslaw_avatar.jpg")} alt={coach.displayName || coach.name} className="w-12 h-12 rounded-full object-cover border-2 border-rose-400/80 shadow-[0_0_12px_rgba(244,63,94,0.3)] shrink-0" />
                                ) : (
                                  <div className="w-11 h-11 rounded-full bg-slate-900 border border-slate-800 flex items-center justify-center text-xl shrink-0 shadow-inner">
                                    <span className="material-symbols-outlined text-slate-400">fitness_center</span>
                                  </div>
                                )}
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center gap-2">
                                    <h4 className="font-black text-lg md:text-xl text-white truncate m-0">{coach.displayName || coach.name}</h4>
                                    <span className="text-xs bg-slate-900 text-slate-300 border border-slate-750 px-2 py-0.5 rounded uppercase font-black">
                                      {coach.language === 'en' ? '🇺🇸 EN' : '🇵🇱 PL'}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-400 font-semibold mt-1 truncate">{coach.email}</p>
                                </div>
                              </div>
                              <div className="flex flex-col gap-1.5 items-end">
                                <span className={`text-[11px] font-black px-3 py-1 rounded-lg uppercase tracking-wider shrink-0 shadow-sm flex items-center gap-1.5 ${isLocked ? 'bg-red-500/20 text-red-300 border border-red-500/50' : 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/50'}`}>
                                  <span className="material-symbols-outlined text-xs">{isLocked ? 'lock' : 'verified'}</span>
                                  <span>{isLocked ? t('superAdmin.lockedAccess', 'LOCKED ACCESS') : t('superAdmin.activeCoach', 'ACTIVE COACH')}</span>
                                </span>
                                {coach.brandCustom && (
                                  <span style={{ borderColor: coach.brandColor || '#6366f1', backgroundColor: `${coach.brandColor || '#6366f1'}20`, color: coach.brandColor || '#6366f1' }} className="text-[10px] font-extrabold px-2 py-0.5 rounded border tracking-wider uppercase flex items-center gap-1.5 max-w-[180px] truncate">
                                    {coach.brandLogo && (coach.brandLogo.startsWith('http://') || coach.brandLogo.startsWith('https://') || coach.brandLogo.startsWith('data:image/') || coach.brandLogo.startsWith('/') || /\.(png|jpg|jpeg|svg|webp|gif)($|\?)/i.test(coach.brandLogo)) ? (
                                      <span className="flex items-center gap-1.5 truncate">
                                        <img src={coach.brandLogo} alt="logo" className="w-4 h-4 object-contain rounded bg-slate-950/40 p-0.5 border border-slate-700/60 shrink-0" />
                                        <span className="truncate">Team Logo</span>
                                      </span>
                                    ) : (
                                      <span className="flex items-center gap-1 truncate">
                                        <span className="material-symbols-outlined text-[12px] shrink-0">palette</span>
                                        <span className="truncate">{coach.brandLogo || 'GymGhost'}</span>
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className="grid grid-cols-2 gap-4 bg-slate-900/90 p-4 rounded-xl border border-slate-800 mb-6 shadow-inner">
                              <div className="border-r border-slate-800 pr-3">
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                                  {t('superAdmin.activeTrainees', 'Active Trainees')}
                                </div>
                                <div className="text-base font-extrabold text-emerald-400 mt-1 flex items-center gap-1.5">
                                  <span className="material-symbols-outlined text-base">group</span>
                                  <span>{activeCount} {t('superAdmin.athletes', 'Athletes')}</span>
                                </div>
                              </div>
                              <div className="pl-1">
                                <div className="text-[10px] font-black text-slate-500 uppercase tracking-wider">
                                  {t('superAdmin.archivedVault', 'Archived Vault')}
                                </div>
                                <div className="text-base font-extrabold text-slate-300 mt-1 flex items-center gap-1.5">
                                  <span className="material-symbols-outlined text-base">archive</span>
                                  <span>{archivedCount} {t('superAdmin.users', 'Users')}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="border-t border-slate-800/80 pt-4 mt-2 flex flex-wrap items-center justify-end gap-2">
                            <button 
                              onClick={() => {
                                setEditingCoachBranding(coach);
                                setBrandCustom(coach.brandCustom || false);
                                setBrandColor(coach.brandColor || "#6366f1");
                                setBrandLogo(coach.brandLogo || "");
                              }}
                              className="bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 text-xs font-semibold px-3 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                            >
                              <span className="material-symbols-outlined text-xs text-rose-400">palette</span>
                              <span>{locale === 'pl' ? 'Edytuj Branding' : 'Edit Branding'}</span>
                            </button>

                            <button 
                              onClick={() => handleChangeUserPassword(coach)}
                              className="bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-800 text-xs font-semibold px-3 py-2 rounded-xl transition flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95"
                            >
                              <span className="material-symbols-outlined text-xs text-indigo-400">key</span>
                              <span>{locale === 'pl' ? 'Zmień Hasło' : 'Change Password'}</span>
                            </button>

                            <button 
                              onClick={() => handleToggleLockCoach(coach.id || coach.email, coach.status)} 
                              className={`text-xs font-bold px-3.5 py-2 rounded-xl border transition flex items-center gap-1.5 cursor-pointer shadow-sm active:scale-95 ${
                                isLocked 
                                  ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/20' 
                                  : 'bg-red-500/10 text-red-400 border-red-500/20 hover:bg-red-500/20'
                              }`}
                            >
                              <span className="material-symbols-outlined text-xs">{isLocked ? 'lock_open' : 'lock'}</span>
                              <span>{isLocked ? (locale === 'pl' ? 'Odblokuj' : 'Unlock') : (locale === 'pl' ? 'Zablokuj' : 'Lock')}</span>
                            </button>

                            <button 
                              onClick={(e) => handleDeleteCoach(coach, e)}
                              className="bg-slate-900 hover:bg-red-500/10 text-slate-500 hover:text-red-400 border border-slate-800 hover:border-red-500/20 p-2 rounded-xl transition cursor-pointer shadow-sm active:scale-90 flex items-center justify-center"
                              title={locale === 'pl' ? "Usuń Trenera" : "Delete Trainer"}
                            >
                              <span className="material-symbols-outlined text-sm">delete</span>
                            </button>
                          </div>
                        </div>
                      );
                    })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: TRAINEE & WORKOUT AUDITOR */}
        {adminTab === 'auditor' && (
          <div className="space-y-6 animate-fadeIn">
            <div className="border-b border-slate-800 pb-4 flex justify-between items-end flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-3xl text-sky-400">group</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                    {t('superAdmin.auditorTitle', 'Global Trainee & Workout Audit Log')}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                  {t('superAdmin.auditorDesc', 'Provision athlete accounts directly to assigned trainers and survey all platform workout entries in real time.')}
                </p>
              </div>

              <div className="flex items-center gap-3 flex-wrap">
                {trainees.length > 0 && (
                  <button
                    onClick={handlePurgeAllTrainees}
                    className="bg-rose-600/90 hover:bg-rose-500 border border-rose-500 text-white font-black px-4 py-2.5 rounded-xl text-xs shadow-lg shadow-rose-500/20 transition cursor-pointer flex items-center gap-1.5 active:scale-95"
                  >
                    <span className="material-symbols-outlined text-sm">delete_sweep</span>
                    <span>{t('superAdmin.wipeAllBtn', 'Wipe All Current Trainees')}</span>
                  </button>
                )}
                <button
                  onClick={() => setIsTraineeFormExpanded(!isTraineeFormExpanded)}
                  className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-black px-5 py-2.5 rounded-xl text-xs sm:text-sm shadow-lg shadow-sky-500/20 transition cursor-pointer flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">{isTraineeFormExpanded ? 'remove_circle' : 'add_circle'}</span>
                  <span>
                    {isTraineeFormExpanded 
                      ? t('superAdmin.closeProvisionTrainee', 'Close Trainee Provisioning') 
                      : t('superAdmin.openProvisionTrainee', 'Provision New Trainee & Assign Coach')}
                  </span>
                </button>
              </div>
            </div>

            {/* Collapsible Trainee Creator & Coach Assignment Form */}
            {isTraineeFormExpanded && (
              <div className="bg-gradient-to-br from-slate-900/95 via-slate-900 to-slate-900 border-2 border-sky-500/50 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6 animate-fadeIn">
                <div className="flex items-center justify-between border-b border-slate-800 pb-3">
                  <h3 className="text-sm md:text-base font-black text-white uppercase tracking-wider m-0 flex items-center gap-2">
                    <span className="material-symbols-outlined text-base text-sky-400">bolt</span>
                    <span>{t('superAdmin.provisionTraineeHeader', 'Provision Trainee & Link Coach Roster')}</span>
                  </h3>
                  <span className="text-[10px] font-black bg-sky-500/10 text-sky-400 border border-sky-500/30 px-3 py-1 rounded-full uppercase tracking-wider">
                    {t('superAdmin.relationalPairing', 'Global Relational Pairing')}
                  </span>
                </div>

                <form onSubmit={handleCreateTrainee} className="grid grid-cols-1 md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-4">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                      {t('superAdmin.traineeNameLabel', 'Trainee Name')}
                    </label>
                    <input type="text" required value={newTraineeName} onChange={e => setNewTraineeName(e.target.value)} placeholder="e.g., Alex Athlete" className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-bold rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-sky-400 shadow-inner" />
                  </div>
                  
                  <div className="md:col-span-4">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                      {t('superAdmin.traineeEmailLabel', 'Trainee Email')}
                    </label>
                    <input type="email" required value={newTraineeEmail} onChange={e => setNewTraineeEmail(e.target.value)} placeholder="e.g., alex@example.com" className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-bold rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-sky-400 shadow-inner" />
                  </div>

                  <div className="md:col-span-4">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                      {t('superAdmin.tempPasswordLabel', 'Temp Password')}
                    </label>
                    <div className="relative flex items-center">
                      <input 
                        type={showTraineePassword ? "text" : "password"} 
                        required 
                        value={newTraineePassword} 
                        onChange={e => setNewTraineePassword(e.target.value)} 
                        placeholder="••••••••" 
                        className="w-full bg-slate-950 border border-slate-750 text-white text-sm font-extrabold rounded-xl pl-3.5 pr-11 py-3.5 focus:outline-none focus:border-sky-400 shadow-inner" 
                      />
                      <button 
                        type="button" 
                        onClick={() => setShowTraineePassword(!showTraineePassword)} 
                        className="absolute right-3 text-slate-400 hover:text-white transition focus:outline-none flex items-center cursor-pointer p-1"
                        title={showTraineePassword ? (locale === 'pl' ? "Ukryj hasło" : "Hide password") : (locale === 'pl' ? "Pokaż hasło" : "Show password")}
                      >
                        <span className="material-symbols-outlined text-lg">{showTraineePassword ? "visibility_off" : "visibility"}</span>
                      </button>
                    </div>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                      {t('superAdmin.preferredLangLabel', 'Default Language')}
                    </label>
                    <select value={newTraineeLanguage} onChange={e => setNewTraineeLanguage(e.target.value)} className="w-full bg-slate-950 border border-slate-750 text-indigo-300 font-extrabold text-sm rounded-xl px-3 py-3.5 focus:outline-none focus:border-sky-400 cursor-pointer shadow-inner">
                      <option value="en">🇺🇸 English (EN)</option>
                      <option value="pl">🇵🇱 Polski (PL)</option>
                    </select>
                  </div>

                  <div className="md:col-span-3">
                    <label className="block text-[11px] font-black text-sky-300 uppercase tracking-wider mb-2">
                      <span className="flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs">wc</span>
                        <span>{locale === 'pl' ? "Płeć (Gender)" : "Gender / Płeć"}</span>
                      </span>
                    </label>
                    <select value={newTraineeGender} onChange={e => setNewTraineeGender(e.target.value)} className="w-full bg-slate-950 border border-slate-750 font-extrabold text-sm rounded-xl px-3 py-3.5 focus:outline-none focus:border-sky-400 cursor-pointer shadow-inner text-sky-300">
                      <option value="male">{locale === 'pl' ? "Mężczyzna" : "Male (Mężczyzna)"}</option>
                      <option value="female">{locale === 'pl' ? "Kobieta" : "Female (Kobieta)"}</option>
                    </select>
                  </div>

                  <div className="md:col-span-6">
                    <label className="block text-[11px] font-black text-sky-400 uppercase tracking-wider mb-2">
                      <span className="flex items-center gap-1.5">
                        <span className="material-symbols-outlined text-xs">support_agent</span>
                        <span>{t('superAdmin.assignCoachLabel', 'Assign to Active Coach Roster')}</span>
                      </span>
                    </label>
                    <select required value={assignedTrainerId} onChange={e => setAssignedTrainerId(e.target.value)} className="w-full bg-slate-950 border border-sky-500/60 text-white font-extrabold text-sm rounded-xl px-3.5 py-3.5 focus:outline-none focus:border-sky-400 cursor-pointer shadow-inner">
                      <option value="">{t('superAdmin.selectCoachOption', '-- Select Registered Coach --')}</option>
                      {coaches.map(c => (
                        <option key={c.id} value={c.id}>{c.displayName || c.name} ({c.email})</option>
                      ))}
                    </select>
                  </div>

                  {/* Third Row: Dedicated Password Requirements Bar & Provision Button */}
                  <div className="md:col-span-12 flex flex-col sm:flex-row sm:items-center justify-between gap-4 pt-2 border-t border-slate-800/80 mt-1">
                    <div className="flex-1 bg-slate-950/60 border border-slate-800/80 rounded-xl p-3 flex flex-wrap items-center gap-2">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                        <span className="material-symbols-outlined text-xs text-slate-400">shield_lock</span>
                        {locale === 'pl' ? "Wymagania hasła:" : "Requirements:"}
                      </span>
                      <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${newTraineePassword.length >= 8 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                        Min. 8 Chars
                      </span>
                      <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[A-Z]/.test(newTraineePassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                        A-Z
                      </span>
                      <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[a-z]/.test(newTraineePassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                        a-z
                      </span>
                      <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[0-9]/.test(newTraineePassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                        0-9
                      </span>
                      <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[@$!%*?&._-]/.test(newTraineePassword) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                        Special Char
                      </span>
                    </div>

                    <div className="w-full sm:w-auto shrink-0">
                      <button type="submit" disabled={isProvisioningTrainee} className="w-full sm:w-[240px] bg-gradient-to-r from-sky-500 to-indigo-600 hover:from-sky-400 hover:to-indigo-500 text-slate-950 font-black py-3.5 px-6 rounded-xl transition shadow-xl shadow-sky-500/25 cursor-pointer active:scale-95 disabled:opacity-50 text-sm flex items-center justify-center gap-1.5">
                        {isProvisioningTrainee ? "..." : (
                          <span className="inline-flex items-center gap-1">
                            <span className="material-symbols-outlined text-sm">bolt</span>
                            <span>{t('superAdmin.provisionAthleteBtn', 'Provision Athlete')} ➔</span>
                          </span>
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
              {/* Trainee Directory */}
              <div className="lg:col-span-6 bg-slate-900/80 border border-slate-800 rounded-2xl p-6 space-y-5 shadow-2xl">
                <div className="flex justify-between items-center flex-wrap gap-3 border-b border-slate-800 pb-3">
                  <h3 className="text-sm font-black text-slate-300 uppercase tracking-wider m-0 flex items-center gap-2">
                    <span className="material-symbols-outlined text-base text-sky-400">group</span>
                    <span>{t('superAdmin.traineeDatabaseHeader', 'Trainee Database')} ({trainees.length})</span>
                  </h3>
                  <input type="text" placeholder={t('superAdmin.searchAthletesPlaceholder', 'Search Athletes...')} value={traineeSearchQuery} onChange={e => setTraineeSearchQuery(e.target.value)} className="bg-slate-950 border border-slate-700 text-white font-semibold text-xs rounded-xl px-3 py-2 focus:outline-none focus:border-sky-400 w-full sm:w-44 shadow-inner" />
                </div>

                {trainees.length === 0 ? (
                  <div className="p-12 text-center text-slate-500 font-medium text-xs italic">
                    {t('superAdmin.noTraineesFound', 'No trainees registered in Firestore. Once you provision athletes above and pair them with coaches, they will appear right here.')}
                  </div>
                ) : (
                  <div className="space-y-4 max-h-[580px] overflow-y-auto pr-1.5">
                    {trainees
                      .filter(t => (t.displayName || t.name || t.email || "").toLowerCase().includes(traineeSearchQuery.toLowerCase()))
                      .map(trainee => {
                        const coachObj = coaches.find(c => c.id === trainee.trainerId || c.email === trainee.trainerId);
                        const isSelected = auditedTraineeName === (trainee.displayName || trainee.name || trainee.email);

                        return (
                          <div 
                            key={trainee.id} 
                            onClick={() => handleAuditTraineeWorkouts(trainee)} 
                            className={`p-5 rounded-2xl border transition-all cursor-pointer space-y-4 shadow-lg select-none ${
                              isSelected ? 'bg-gradient-to-br from-sky-950/80 via-slate-900 to-slate-900 border-sky-400 shadow-[0_0_20px_rgba(56,189,248,0.25)] ring-1 ring-sky-400/40' : 'bg-slate-950/80 hover:bg-slate-900/90 border-slate-800 hover:border-slate-700'
                            }`}
                          >
                            {/* TOP ROW: Avatar + Name & Live Dot + Gender */}
                            <div className="flex items-center justify-between gap-3 border-b border-slate-800/80 pb-3">
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-12 h-12 rounded-full border-2 border-sky-400/80 bg-slate-900 overflow-hidden shrink-0 shadow-md flex items-center justify-center">
                                  {trainee.avatarUrl ? (
                                    <img src={trainee.avatarUrl} alt={trainee.displayName || trainee.name} className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full bg-gradient-to-br from-sky-600 to-indigo-900 flex items-center justify-center text-xs font-black text-white uppercase tracking-wider select-none">
                                      {(() => {
                                        const nameStr = trainee.displayName || trainee.name || trainee.email || "";
                                        const parts = nameStr.trim().split(/\s+/);
                                        if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
                                        if (parts[0] && parts[0].length >= 2) return parts[0].substring(0, 2).toUpperCase();
                                        return "GG";
                                      })()}
                                    </div>
                                  )}
                                </div>

                                <div className="min-w-0">
                                  <h4 className="font-black text-base md:text-lg text-white m-0 tracking-tight flex items-center gap-2">
                                    <span className="truncate">{trainee.displayName || trainee.name}</span>
                                    <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse shrink-0" title="Live Sync Active" />
                                  </h4>
                                  <span className={trainee.gender === 'female' ? 'text-pink-400 font-extrabold text-[11px] inline-flex items-center gap-1 mt-0.5' : 'text-sky-400 font-extrabold text-[11px] inline-flex items-center gap-1 mt-0.5'}>
                                    <span className="material-symbols-outlined text-[13px]">{trainee.gender === 'female' ? 'female' : 'male'}</span>
                                    <span>{trainee.gender === 'female' ? (locale === 'pl' ? 'Kobieta' : 'Female') : (locale === 'pl' ? 'Mężczyzna' : 'Male')}</span>
                                  </span>
                                </div>
                              </div>

                              {/* Selection status badge */}
                              {isSelected && (
                                <span className="bg-sky-500/20 text-sky-300 border border-sky-500/40 px-2.5 py-1 rounded-full text-[10px] font-black uppercase tracking-wider shrink-0 flex items-center gap-1">
                                  <span className="w-1.5 h-1.5 rounded-full bg-sky-400 animate-ping" />
                                  <span>Auditing</span>
                                </span>
                              )}
                            </div>

                            {/* MIDDLE ROW: Full Email & Coach Pairing Pill */}
                            <div className="space-y-2">
                              <div className="bg-slate-900/90 border border-slate-800 rounded-xl px-3.5 py-2 flex items-center gap-2 text-xs font-mono text-slate-200">
                                <span className="material-symbols-outlined text-slate-400 text-sm shrink-0">mail</span>
                                <span className="truncate font-semibold text-slate-200 select-all" title={trainee.email}>{trainee.email}</span>
                              </div>

                              <div className="bg-slate-900/60 border border-slate-800/80 rounded-xl px-3 py-1.5 flex items-center justify-between gap-2 text-[11px]">
                                <span className="text-slate-400 font-bold flex items-center gap-1">
                                  <span className="material-symbols-outlined text-xs text-rose-400">sports</span>
                                  <span>{locale === 'pl' ? "Przypisany Trener:" : "Assigned Coach:"}</span>
                                </span>
                                <span className="font-extrabold text-white truncate">
                                  {coachObj 
                                    ? (coachObj.displayName || coachObj.name || coachObj.email) 
                                    : (trainee.trainerId === 'trainer_987654' 
                                        ? "Wiesław Piątek" 
                                        : (trainee.trainerId ? trainee.trainerId : (locale === 'pl' ? "Brak Trenera" : "Unassigned")))}
                                </span>
                              </div>
                            </div>

                            {/* BOTTOM ROW: Action Buttons Toolbar */}
                            <div className="flex items-center justify-between gap-2 pt-1 border-t border-slate-800/60">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleChangeUserPassword(trainee);
                                  }}
                                  title={locale === 'pl' ? "Zmień hasło dla konta zawodnika" : "Change trainee password"}
                                  className="px-3 py-1.5 bg-amber-500/15 hover:bg-amber-500 text-amber-300 hover:text-slate-950 border border-amber-500/30 rounded-xl text-xs font-extrabold transition cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-sm"
                                >
                                  <span className="material-symbols-outlined text-xs">key</span>
                                  <span>{locale === 'pl' ? "Hasło" : "Password"}</span>
                                </button>

                                <button
                                  type="button"
                                  onClick={(e) => handleDeleteTrainee(trainee, e)}
                                  title={t('superAdmin.deleteTraineeTooltip', 'Delete this trainee from database')}
                                  className="px-3 py-1.5 bg-rose-500/15 hover:bg-rose-600 text-rose-300 hover:text-white border border-rose-500/30 rounded-xl text-xs font-extrabold transition cursor-pointer flex items-center gap-1.5 active:scale-95 shadow-sm"
                                >
                                  <span className="material-symbols-outlined text-xs">delete</span>
                                  <span>{locale === 'pl' ? "Usuń" : "Delete"}</span>
                                </button>
                              </div>

                              <span className="text-xs font-black text-sky-400 hover:text-sky-300 flex items-center gap-1">
                                <span>{t('superAdmin.auditBtn', 'Audit Feed')}</span>
                                <span className="material-symbols-outlined text-xs">arrow_forward</span>
                              </span>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>

              {/* Audit Log Output panel */}
              <div className="lg:col-span-6 bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-8 shadow-2xl space-y-6">
                <div className="border-b border-slate-800 pb-4 flex justify-between items-center flex-wrap gap-4">
                  <div>
                    <h3 className="text-lg md:text-xl font-black text-white m-0 flex items-center gap-2">
                      <span className="material-symbols-outlined text-xl text-indigo-400">history</span>
                      <span>{t('superAdmin.auditFeedHeader', 'Audit Workout Feed:')} <span className="text-sky-400">{auditedTraineeName || t('superAdmin.selectTrainee', 'Select Trainee')}</span></span>
                    </h3>
                    <span className="text-xs text-slate-400 font-medium mt-1 block">
                      {t('superAdmin.auditFeedDesc', 'Full security surveillance of all sets, repetitions, and performance entries.')}
                    </span>
                  </div>
                  {selectedTraineeWorkouts.length > 0 && (
                    <span className="bg-sky-500/20 text-sky-300 border border-sky-500/40 px-3 py-1 rounded-full text-xs font-black uppercase">
                      ✓ {selectedTraineeWorkouts.length} {t('superAdmin.loggedSessions', 'Logged Sessions')}
                    </span>
                  )}
                </div>

                <div className="space-y-4 max-h-[540px] overflow-y-auto pr-1">
                  {selectedTraineeWorkouts.length === 0 ? (
                    <div className="p-16 text-center bg-slate-950/60 border border-dashed border-slate-800 rounded-2xl text-slate-400 font-medium text-sm">
                      <span className="material-symbols-outlined text-4xl text-slate-600 block mx-auto mb-2">folder_open</span>
                      {t('superAdmin.noWorkoutsLoaded', 'No workout logs loaded yet. Click any trainee from the directory on the left to review their database writes and exercise volume.')}
                    </div>
                  ) : (
                    [...selectedTraineeWorkouts]
                      .sort((a, b) => {
                        const dateA = (a.date || a.createdAt || "").toString();
                        const dateB = (b.date || b.createdAt || "").toString();
                        return dateB.localeCompare(dateA);
                      })
                      .map(w => (
                      <div key={w.id} className="bg-slate-950/90 border border-slate-800 hover:border-sky-500/40 p-6 rounded-2xl shadow-xl transition-all space-y-4">
                        <div className="flex justify-between items-center text-sm font-extrabold text-white border-b border-slate-800 pb-3 flex-wrap gap-2">
                          <span className="flex items-center gap-2 bg-slate-900 px-3 py-1 rounded-lg border border-slate-800">
                            <span className="material-symbols-outlined text-sm text-sky-400">event</span>
                            <span>{t('superAdmin.dateLabel', 'Date:')} <strong className="text-sky-400 font-black">{w.date || t('superAdmin.today', 'Today')}</strong></span>
                          </span>
                          <span className="bg-purple-500/20 text-purple-300 border border-purple-500/40 px-3 py-1 rounded-lg text-xs font-black flex items-center gap-1.5">
                            <span className="material-symbols-outlined text-xs text-amber-400">local_fire_department</span>
                            <span>{t('superAdmin.rpeScore', 'RPE Score:')} {w.rpe || "N/A"}/10</span>
                          </span>
                        </div>
                        
                        <p className="text-[11px] text-slate-500 font-bold uppercase tracking-widest m-0">
                          {t('superAdmin.workoutId', 'Workout Document ID:')} <code className="text-slate-400 bg-slate-900 px-1.5 py-0.5 rounded">{w.id}</code>
                        </p>
                        
                        <div className="space-y-2 pt-1">
                          <span className="text-[11px] font-black text-slate-400 uppercase tracking-wider block mb-1">
                            {t('superAdmin.movementsCompleted', 'Movements Completed:')}
                          </span>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                            {(w.exercises || []).map((ex, i) => (
                              <div key={i} className="bg-slate-900/90 border border-slate-800 p-3 rounded-xl flex items-center justify-between gap-2 shadow-inner">
                                <span className="font-extrabold text-slate-200 text-xs truncate">• {ex.name}</span>
                                <span className="text-[10px] font-black bg-sky-500/10 text-sky-300 border border-sky-500/30 px-2 py-0.5 rounded shrink-0">
                                  {ex.sets?.length || ex.setsCount || 3} {t('superAdmin.setsCount', 'Sets')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* TAB 3: SETTINGS */}
        {adminTab === 'settings' && (
          <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-6 md:p-8 max-w-2xl animate-fadeIn shadow-2xl space-y-6">
            <div className="border-b border-slate-800 pb-3">
              <h3 className="text-xl font-black text-white uppercase tracking-wider m-0 flex items-center gap-2">
                <span className="material-symbols-outlined text-xl text-rose-400">settings</span>
                <span>{t('superAdmin.settingsTitle', 'Global Platform & Language Settings')}</span>
              </h3>
              <p className="text-slate-400 text-xs font-semibold mt-1">
                {t('superAdmin.settingsDesc', 'Configure system-wide operational interface language and review security override status.')}
              </p>
            </div>

            <div className="space-y-6">
              <div className="bg-slate-950/80 border border-slate-800 p-5 rounded-2xl space-y-3">
                <label className="block text-xs font-black text-slate-300 tracking-wider uppercase">
                  {t('superAdmin.preferredPortalLang', 'Preferred Portal Language & Localization')}
                </label>
                <p className="text-xs text-slate-500 m-0 font-medium">
                  {t('superAdmin.langSub', 'Select your preferred interface language for Super Admin diagnostics and surveillance dashboards:')}
                </p>
                <div className="flex gap-3 pt-1">
                  <button 
                    onClick={() => changeLanguage('en')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      locale === 'en' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇺🇸</span> {t('superAdmin.englishBtn', 'English (ENG)')}
                  </button>
                  <button 
                    onClick={() => changeLanguage('pl')} 
                    className={`px-6 py-3 text-xs sm:text-sm font-black rounded-xl transition cursor-pointer flex items-center gap-2.5 shadow-lg active:scale-95 ${
                      locale === 'pl' ? 'bg-indigo-600 text-white shadow-indigo-500/30' : 'bg-slate-900 text-slate-400 hover:text-white border border-slate-800'
                    }`}
                  >
                    <span>🇵🇱</span> {t('superAdmin.polishBtn', 'Polski (PL)')}
                  </button>
                </div>
              </div>

              <div className="bg-slate-950/80 border border-rose-500/30 p-5 rounded-2xl space-y-3">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse" />
                  <strong className="text-sm font-black text-white uppercase tracking-wide">
                    {t('superAdmin.absoluteBypassHeader', 'Absolute Firebase Auth & Rules Bypass Active')}
                  </strong>
                </div>
                <p className="text-xs text-slate-400 leading-relaxed m-0 font-medium">
                  {t('superAdmin.absoluteBypassDesc', "Your current session is operating under the verified role: 'admin' credential token. You maintain absolute override capabilities across all tenant databases, coach libraries, multi-day training cycles, and trainee diary logs.")}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TAB 4: GLOBAL LIBRARY (STANDARD PLATFORM CONTENT) */}
        {adminTab === 'global_library' && (
          <div className="space-y-8 animate-fadeIn">
            <div className="border-b border-slate-800 pb-4 flex justify-between items-end flex-wrap gap-4">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <span className="material-symbols-outlined text-3xl text-rose-400">local_library</span>
                  <h2 className="text-2xl md:text-3xl font-black text-white m-0 tracking-tight leading-none">
                    {locale === 'pl' ? "Globalna Biblioteka Standardowa" : "Standard Global Library"}
                  </h2>
                </div>
                <p className="text-slate-400 text-sm md:text-base mt-1.5 m-0 font-medium">
                  {locale === 'pl' 
                    ? "Zarządzaj ogólnodostępnymi (Standard) ćwiczeniami, szablonami oraz planami wielodniowymi widocznymi dla wszystkich trenerów i zawodników." 
                    : "Manage platform-wide standard exercises, routine templates, and multi-day plans available across all tenants."}
                </p>
              </div>

              {/* Sub-tab Navigation */}
              <div className="flex items-center gap-1.5 bg-slate-900 border border-slate-800 p-1.5 rounded-2xl shadow-inner">
                <button
                  onClick={() => setGlobalSubTab('exercises')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${
                    globalSubTab === 'exercises' ? 'bg-rose-600 text-white shadow-md' : 'text-slate-400 hover:text-white bg-transparent border-0'
                  }`}
                >
                  🌐 {locale === 'pl' ? 'Ćwiczenia Globalne' : 'Global Exercises'} ({allExercises.filter(e => e.isGlobal || e.trainerId === 'global' || e.trainerId === 'all').length})
                </button>
                <button
                  onClick={() => setGlobalSubTab('routines')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${
                    globalSubTab === 'routines' ? 'bg-rose-600 text-white shadow-md' : 'text-slate-400 hover:text-white bg-transparent border-0'
                  }`}
                >
                  📋 {locale === 'pl' ? 'Szablony Treningowe' : 'Global Routines'} ({allRoutines.filter(r => r.isGlobal || r.assignedTo === 'global' || r.assignedClientId === 'global' || r.trainerId === 'global').length})
                </button>
                <button
                  onClick={() => setGlobalSubTab('plans')}
                  className={`px-4 py-2 rounded-xl text-xs font-black transition cursor-pointer ${
                    globalSubTab === 'plans' ? 'bg-rose-600 text-white shadow-md' : 'text-slate-400 hover:text-white bg-transparent border-0'
                  }`}
                >
                  📅 {locale === 'pl' ? 'Plany Wielodniowe' : 'Global Plans'} ({allPlans.filter(p => p.isGlobal || p.assignedTo === 'global' || p.assignedClientId === 'global' || p.trainerId === 'global').length})
                </button>
              </div>
            </div>

            {/* SUB-TAB 1: GLOBAL EXERCISES */}
            {globalSubTab === 'exercises' && (
              <div className="space-y-6">
                {/* Form to add new Global Exercise */}
                <div className="bg-slate-900 border-2 border-rose-500/30 rounded-2xl p-6 shadow-xl space-y-4">
                  <div className="flex items-center justify-between flex-wrap gap-3">
                    <h3 className="text-sm font-black text-white uppercase tracking-wider flex items-center gap-2 m-0">
                      <span className="material-symbols-outlined text-rose-400 text-base">add_circle</span>
                      <span>{locale === 'pl' ? "Dodaj Nowe Ćwiczenie (Standard)" : "Add New Standard Exercise"}</span>
                    </h3>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setShowGoogleSheetImportCard(!showGoogleSheetImportCard)}
                        className="bg-slate-950 hover:bg-slate-800 text-emerald-400 hover:text-emerald-300 border border-emerald-500/30 px-3.5 py-1.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-95"
                      >
                        <span className="material-symbols-outlined text-sm">table_chart</span>
                        <span>{locale === 'pl' ? "📊 Import z Google Sheets" : "📊 Import from Google Sheets"}</span>
                      </button>

                      {/* TAB SWITCHER PL / EN */}
                      <div className="flex items-center gap-2 bg-slate-950 p-1 rounded-xl border border-slate-800">
                        <button
                          type="button"
                          onClick={() => setGlobalExLangTab('pl')}
                          className={`px-3 py-1 rounded-lg text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
                            globalExLangTab === 'pl' 
                              ? 'bg-rose-500 text-white shadow-md' 
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <span>Polski (PL)</span>
                        </button>
                        <button
                          type="button"
                          onClick={() => setGlobalExLangTab('en')}
                          className={`px-3 py-1 rounded-lg text-xs font-black transition cursor-pointer flex items-center gap-1.5 ${
                            globalExLangTab === 'en' 
                              ? 'bg-indigo-500 text-white shadow-md' 
                              : 'text-slate-400 hover:text-white'
                          }`}
                        >
                          <span>English (EN)</span>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* COLLAPSIBLE GOOGLE SHEETS IMPORT SECTION */}
                  {showGoogleSheetImportCard && (
                    <div className="bg-slate-950 border border-emerald-500/40 rounded-2xl p-5 space-y-4 animate-fadeIn shadow-2xl">
                      <div className="flex items-center justify-between border-b border-slate-800 pb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-emerald-400 text-xl">table_chart</span>
                          <h4 className="text-sm font-black text-white uppercase tracking-wider m-0">
                            {locale === 'pl' ? "Import Masowy Ćwiczeń z Google Sheets" : "Direct Google Sheets Exercise Import"}
                          </h4>
                        </div>

                        <a
                          href={googleSheetUrlInput}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-emerald-500/15 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 px-3 py-1.5 rounded-xl text-xs font-black transition cursor-pointer flex items-center gap-1.5 shadow-sm active:scale-95"
                        >
                          <span className="material-symbols-outlined text-sm">open_in_new</span>
                          <span>{locale === 'pl' ? "Otwórz w Google Sheets" : "Open Google Sheet"}</span>
                        </a>
                      </div>

                      <div className="text-xs text-slate-300 space-y-2 font-medium">
                        <p className="m-0">
                          {locale === 'pl' 
                            ? "Wklej link do Arkusza Google Sheets z ćwiczeniami (wymagany dostęp 'Każda osoba mająca link może wyświetlać'):" 
                            : "Paste your Google Sheets link (must have 'Anyone with the link can view' permission):"}
                        </p>

                        <div className="flex flex-col sm:flex-row items-center gap-2.5 pt-1">
                          <input
                            type="url"
                            value={googleSheetUrlInput}
                            onChange={(e) => setGoogleSheetUrlInput(e.target.value)}
                            placeholder="https://docs.google.com/spreadsheets/d/..."
                            className="w-full bg-slate-900 border border-slate-800 focus:border-emerald-500/60 text-white font-mono text-xs rounded-xl px-4 py-3 outline-none shadow-inner"
                          />

                          <button
                            type="button"
                            disabled={isImportingGoogleSheet}
                            onClick={() => handleSyncGoogleSheet()}
                            className="w-full sm:w-auto bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs px-6 py-3 rounded-xl transition cursor-pointer shadow-lg active:scale-95 flex items-center justify-center gap-1.5 shrink-0 disabled:opacity-50"
                          >
                            {isImportingGoogleSheet ? (
                              <>
                                <span className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                <span>{locale === 'pl' ? "Importowanie..." : "Importing..."}</span>
                              </>
                            ) : (
                              <>
                                <span className="material-symbols-outlined text-sm">sync</span>
                                <span>{locale === 'pl' ? "⚡ Importuj z Google Sheets" : "⚡ Import from Google Sheets"}</span>
                              </>
                            )}
                          </button>
                        </div>

                        <p className="text-[11px] text-amber-400 font-extrabold m-0 pt-1 flex items-center gap-1">
                          <span className="material-symbols-outlined text-xs">verified</span>
                          <span>{locale === 'pl' ? "Automatyczna weryfikacja duplikatów: istniejące ćwiczenia w bazie zostaną pominięte." : "Automatic duplicate checking: existing exercises will be skipped."}</span>
                        </p>
                      </div>

                      {googleSheetImportReport && (
                        <div className="bg-slate-900 border border-slate-800 p-4 rounded-xl space-y-2 animate-fadeIn text-xs">
                          <div className="flex items-center gap-2 font-black text-emerald-400">
                            <span className="material-symbols-outlined text-base">check_circle</span>
                            <span>{locale === 'pl' ? `Pomyślnie zaimportowano ${googleSheetImportReport.imported} nowych ćwiczeń z Google Sheets!` : `Successfully imported ${googleSheetImportReport.imported} new exercises from Google Sheets!`}</span>
                          </div>
                          {googleSheetImportReport.duplicates > 0 && (
                            <div className="text-amber-400 font-bold">
                              {locale === 'pl' ? `Pominięto ${googleSheetImportReport.duplicates} duplikatów (już istnieją w bazie).` : `Skipped ${googleSheetImportReport.duplicates} duplicates (already present in database).`}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Modern Two-Column Exercise Builder Form for Global Exercises */}
                  <div className="pt-2">
                    <ExerciseFormBuilder
                      isGlobal={true}
                      isUploading={isUploadingImage}
                      lang={locale}
                      onSubmit={async (formData) => {
                        setIsUploadingImage(true);
                        try {
                          let uploadedUrl = formData.imageUrlInput || "";
                          let imagePath = "";

                          if (formData.imageFile) {
                            const fileName = `${Date.now()}_${formData.imageFile.name}`;
                            imagePath = `exercises/global/${fileName}`;

                            const convertToBase64 = (fileObj) => new Promise((resolve) => {
                              const reader = new FileReader();
                              reader.onload = (ev) => resolve(ev.target.result || "");
                              reader.onerror = () => resolve("");
                              reader.readAsDataURL(fileObj);
                            });

                            try {
                              const storageRef = ref(storage, imagePath);
                              const gcsPromise = uploadBytes(storageRef, formData.imageFile).then(() => getDownloadURL(storageRef));
                              const timeoutPromise = new Promise((_, reject) => setTimeout(() => reject(new Error("Storage timeout")), 5000));
                              uploadedUrl = await Promise.race([gcsPromise, timeoutPromise]);
                            } catch (storageErr) {
                              console.warn("GCS upload bypassed/timed out, using inline image fallback:", storageErr.message);
                              uploadedUrl = await convertToBase64(formData.imageFile);
                            }
                          }

                          const exId = 'ex_global_' + Date.now();
                          const primaryName = formData.namePL || formData.nameEN || formData.name;
                          const primaryInstruction = formData.instructionsPL || formData.instructionsEN;

                          const payload = {
                            id: exId,
                            docId: exId,
                            exerciseId: exId.replace('ex_global_', ''),
                            name: primaryName,
                            namePL: formData.namePL || primaryName,
                            nameEN: formData.nameEN || primaryName,
                            name_translations: {
                              en: formData.nameEN || primaryName,
                              pl: formData.namePL || primaryName
                            },
                            category: formData.bodyPart,
                            body_part: formData.bodyPart,
                            bodyPart: formData.bodyPart,
                            equipment: formData.equipment,
                            target: formData.target || formData.subPart || '',
                            subPart: formData.subPart || formData.target || '',
                            muscle_group: formData.muscle_group || formData.bodyPart,
                            secondary_muscles: Array.isArray(formData.secondary_muscles) ? formData.secondary_muscles : [],
                            instructions: {
                              en: formData.instructionsEN || '',
                              pl: formData.instructionsPL || ''
                            },
                            instruction_steps: {
                              en: (formData.instructionsEN || '').split('\n').filter(Boolean),
                              pl: (formData.instructionsPL || '').split('\n').filter(Boolean)
                            },
                            hints: primaryInstruction,
                            notes: primaryInstruction,
                            hintsPL: formData.instructionsPL || primaryInstruction,
                            hintsEN: formData.instructionsEN || primaryInstruction,
                            media_id: exId,
                            image: uploadedUrl,
                            gif_url: uploadedUrl,
                            imageUrl: uploadedUrl,
                            imagePath: imagePath,
                            youtubeUrl: formData.youtubeUrl || '',
                            videoUrl: formData.youtubeUrl || '',
                            attribution: "Gym visual",
                            created_at: new Date().toISOString(),
                            isGlobal: true,
                            trainerId: "global",
                            createdAt: Date.now()
                          };

                          await setDoc(doc(db, "exercises", exId), payload);
                          alert(locale === 'pl' ? `✅ Dodano nowe ćwiczenie globalne "${primaryName}"!` : `✅ Added new global exercise "${primaryName}"!`);
                        } catch (err) {
                          console.error("Error uploading exercise media:", err);
                          alert("Upload error: " + err.message);
                        } finally {
                          setIsUploadingImage(false);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Search & Body Part Filter Control Bar for Global Exercises */}
                {(() => {
                  const globalExercises = allExercises.filter(e => e.isGlobal || e.trainerId === 'global' || e.trainerId === 'all');
                  const filteredGlobalExercises = globalExercises.filter(ex => {
                    if (globalExFilterMuscle !== 'ALL') {
                      const exMuscle = (ex.bodyPart || ex.category || ex.targetMuscle || "").toLowerCase();
                      const filterMuscle = globalExFilterMuscle.toLowerCase();
                      if (!exMuscle.includes(filterMuscle)) return false;
                    }
                    if (globalExFilterEquipment !== 'ALL') {
                      const exEq = (ex.equipment || "").toLowerCase().trim();
                      const filterEq = globalExFilterEquipment.toLowerCase().trim();
                      if (exEq !== filterEq) return false;
                    }
                    if (globalExSearchQuery.trim() !== "") {
                      const query = globalExSearchQuery.toLowerCase().trim();
                      const name = (ex.name || "").toLowerCase();
                      const namePL = (ex.namePL || "").toLowerCase();
                      const nameEN = (ex.nameEN || "").toLowerCase();
                      const subPart = (ex.subPart || ex.target || "").toLowerCase();
                      const equipment = (ex.equipment || "").toLowerCase();
                      const hints = (ex.hints || ex.hintsPL || ex.hintsEN || "").toLowerCase();
                      const bodyPart = (ex.bodyPart || ex.category || "").toLowerCase();
                      const matches = name.includes(query) || namePL.includes(query) || nameEN.includes(query) || subPart.includes(query) || equipment.includes(query) || hints.includes(query) || bodyPart.includes(query);
                      if (!matches) return false;
                    }
                    return true;
                  });

                  return (
                    <div className="space-y-4">
                      {/* Search & Filter Control Bar */}
                      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-4 shadow-xl space-y-3">
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 flex-wrap">
                          
                          {/* Search Input */}
                          <div className="relative flex-1 min-w-[200px]">
                            <span className="material-symbols-outlined absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 text-lg pointer-events-none">
                              search
                            </span>
                            <input
                              type="text"
                              value={globalExSearchQuery}
                              onChange={(e) => setGlobalExSearchQuery(e.target.value)}
                              placeholder={locale === 'pl' ? "Szukaj ćwiczenia po nazwie, partii lub sprzęcie..." : "Search exercises by name, muscle, equipment..."}
                              className="w-full bg-slate-950 border border-slate-800 focus:border-rose-500/60 text-white font-bold text-xs rounded-xl pl-10 pr-10 py-2.5 outline-none shadow-inner"
                            />
                            {globalExSearchQuery && (
                              <button
                                type="button"
                                onClick={() => setGlobalExSearchQuery("")}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white cursor-pointer"
                              >
                                <span className="material-symbols-outlined text-sm">close</span>
                              </button>
                            )}
                          </div>

                          {/* Muscle Filter Dropdown */}
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-black text-slate-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
                              <span className="material-symbols-outlined text-sm text-rose-400">filter_alt</span>
                              <span>{locale === 'pl' ? "Partia:" : "Muscle:"}</span>
                            </label>
                            <select
                              value={globalExFilterMuscle}
                              onChange={(e) => setGlobalExFilterMuscle(e.target.value)}
                              className="bg-slate-950 border border-slate-800 text-white font-bold text-xs rounded-xl px-3 py-2.5 outline-none cursor-pointer shadow-inner"
                            >
                              <option value="ALL">{locale === 'pl' ? "Wszystkie Partie" : "All Muscles"}</option>
                              <option value="chest">{locale === 'pl' ? "Klatka piersiowa" : "Chest"}</option>
                              <option value="back">{locale === 'pl' ? "Plecy" : "Back"}</option>
                              <option value="shoulders">{locale === 'pl' ? "Barki" : "Shoulders"}</option>
                              <option value="upper arms">{locale === 'pl' ? "Ramiona" : "Upper Arms"}</option>
                              <option value="lower arms">{locale === 'pl' ? "Przedramiona" : "Lower Arms"}</option>
                              <option value="upper legs">{locale === 'pl' ? "Uda i Pośladki" : "Upper Legs / Glutes"}</option>
                              <option value="lower legs">{locale === 'pl' ? "Łydki" : "Lower Legs"}</option>
                              <option value="waist">{locale === 'pl' ? "Brzuch / Pas" : "Waist / Core"}</option>
                              <option value="cardio">{locale === 'pl' ? "Kardio" : "Cardio"}</option>
                              <option value="neck">{locale === 'pl' ? "Szyja" : "Neck"}</option>
                            </select>
                          </div>

                          {/* Equipment Filter Dropdown */}
                          <div className="flex items-center gap-2">
                            <label className="text-xs font-black text-slate-400 uppercase tracking-wider shrink-0 flex items-center gap-1">
                              <span className="material-symbols-outlined text-sm text-indigo-400">fitness_center</span>
                              <span>{locale === 'pl' ? "Sprzęt:" : "Equipment:"}</span>
                            </label>
                            <select
                              value={globalExFilterEquipment}
                              onChange={(e) => setGlobalExFilterEquipment(e.target.value)}
                              className="bg-slate-950 border border-slate-800 text-white font-bold text-xs rounded-xl px-3 py-2.5 outline-none cursor-pointer shadow-inner"
                            >
                              <option value="ALL">{locale === 'pl' ? "Wszystkie Sprzęty" : "All Equipment"}</option>
                              <option value="body weight">{locale === 'pl' ? "Ciężar ciała" : "Body Weight"}</option>
                              <option value="dumbbell">{locale === 'pl' ? "Hantle" : "Dumbbell"}</option>
                              <option value="cable">{locale === 'pl' ? "Wyciąg" : "Cable"}</option>
                              <option value="barbell">{locale === 'pl' ? "Sztanga" : "Barbell"}</option>
                              <option value="leverage machine">{locale === 'pl' ? "Maszyna" : "Leverage Machine"}</option>
                              <option value="band">{locale === 'pl' ? "Guma oporowa" : "Band"}</option>
                              <option value="smith machine">{locale === 'pl' ? "Suwnica Smitha" : "Smith Machine"}</option>
                              <option value="kettlebell">Kettlebell</option>
                              <option value="weighted">{locale === 'pl' ? "Z dociążeniem" : "Weighted"}</option>
                              <option value="stability ball">{locale === 'pl' ? "Piłka gimnastyczna" : "Stability Ball"}</option>
                              <option value="ez barbell">{locale === 'pl' ? "Sztanga łamana" : "EZ Barbell"}</option>
                              <option value="other">{locale === 'pl' ? "Inny" : "Other"}</option>
                            </select>
                          </div>

                        </div>

                        {/* Muscle & Equipment Quick Category Chips */}
                        <div className="flex items-center gap-1.5 flex-wrap pt-1 border-t border-slate-800/60">
                          {[
                            { id: 'ALL', label: locale === 'pl' ? 'Wszystkie' : 'All' },
                            { id: 'chest', label: locale === 'pl' ? 'Klatka' : 'Chest' },
                            { id: 'back', label: locale === 'pl' ? 'Plecy' : 'Back' },
                            { id: 'shoulders', label: locale === 'pl' ? 'Barki' : 'Shoulders' },
                            { id: 'upper arms', label: locale === 'pl' ? 'Ramiona' : 'Upper Arms' },
                            { id: 'lower arms', label: locale === 'pl' ? 'Przedramiona' : 'Lower Arms' },
                            { id: 'upper legs', label: locale === 'pl' ? 'Uda' : 'Upper Legs' },
                            { id: 'lower legs', label: locale === 'pl' ? 'Łydki' : 'Lower Legs' },
                            { id: 'waist', label: locale === 'pl' ? 'Brzuch' : 'Waist' },
                            { id: 'cardio', label: locale === 'pl' ? 'Kardio' : 'Cardio' },
                            { id: 'neck', label: locale === 'pl' ? 'Szyja' : 'Neck' }
                          ].map((chip) => (
                            <button
                              key={chip.id}
                              type="button"
                              onClick={() => setGlobalExFilterMuscle(chip.id)}
                              className={`px-3 py-1 rounded-lg text-[11px] font-extrabold transition cursor-pointer active:scale-95 ${
                                globalExFilterMuscle === chip.id
                                  ? 'bg-rose-600 text-white shadow-md'
                                  : 'bg-slate-950 text-slate-400 hover:text-white border border-slate-800'
                              }`}
                            >
                              {chip.label}
                            </button>
                          ))}

                          <div className="ml-auto text-[11px] font-extrabold text-slate-400">
                            {locale === 'pl' 
                              ? `Pokazano: ${filteredGlobalExercises.length} z ${globalExercises.length}` 
                              : `Showing: ${filteredGlobalExercises.length} of ${globalExercises.length}`}
                          </div>
                        </div>
                      </div>

                      {/* List of Filtered Global Exercises */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                        {filteredGlobalExercises.length === 0 ? (
                          <div className="col-span-full bg-slate-900 border border-slate-800 rounded-2xl p-8 text-center space-y-2">
                            <span className="material-symbols-outlined text-4xl text-slate-600">search_off</span>
                            <h4 className="text-sm font-black text-slate-300 m-0">
                              {locale === 'pl' ? "Brak ćwiczeń spełniających kryteria wyszukiwania" : "No exercises match your search filters"}
                            </h4>
                            <button
                              type="button"
                              onClick={() => { setGlobalExSearchQuery(""); setGlobalExFilterMuscle("ALL"); }}
                              className="text-xs font-bold text-rose-400 hover:underline cursor-pointer bg-transparent border-0"
                            >
                              {locale === 'pl' ? "Resetuj filtry" : "Reset filters"}
                            </button>
                          </div>
                        ) : (
                          filteredGlobalExercises.map((ex) => {
                            const isEditing = editingGlobalExId === ex.id;

                            if (isEditing) {
                              return (
                                <div key={ex.id} className="col-span-full my-4">
                                  <ExerciseFormBuilder 
                                    initialData={ex}
                                    isGlobal={true}
                                    isEdit={true}
                                    onSubmit={async (formData) => {
                                      setIsUpdatingGlobalExImage(true);
                                      try {
                                        const docRef = doc(db, 'exercises', ex.id);
                                        await setDoc(docRef, { ...formData, isGlobal: true, trainerId: 'global', updatedAt: Date.now() }, { merge: true });
                                        setEditingGlobalExId(null);
                                        alert(locale === 'pl' ? `✏️ Zaktualizowano ćwiczenie globalne!` : `✏️ Updated global exercise!`);
                                      } catch (err) {
                                        console.error("Global exercise update error:", err);
                                        alert("Update error: " + err.message);
                                      } finally {
                                        setIsUpdatingGlobalExImage(false);
                                      }
                                    }}
                                    onCancel={() => setEditingGlobalExId(null)}
                                    isUploading={isUpdatingGlobalExImage}
                                    lang={locale}
                                  />
                                </div>
                              );
                            }

                            return (
                              <div key={ex.id} className="bg-slate-900 border border-slate-800 p-4 rounded-2xl space-y-3 flex flex-col justify-between shadow-lg">
                                <div className="space-y-2">
                                  <div className="flex items-center justify-between gap-2 border-b border-slate-800/80 pb-2.5">
                                    <span className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[9px] font-black uppercase px-2 py-0.5 rounded-md">
                                      🌐 GLOBALNY STANDARD
                                    </span>
                                    
                                    <div className="flex items-center gap-1">
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setEditingGlobalExId(ex.id);
                                        }}
                                        className="text-sky-400 hover:text-sky-300 transition bg-slate-950 hover:bg-slate-800 border border-slate-800 p-1.5 rounded-lg cursor-pointer flex items-center justify-center"
                                        title={locale === 'pl' ? "Edytuj" : "Edit"}
                                      >
                                        <span className="material-symbols-outlined text-xs">edit</span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={async () => {
                                          if (window.confirm(locale === 'pl' ? `Usuń z Globalnej Biblioteki: ${ex.name}?` : `Delete global exercise: ${ex.name}?`)) {
                                            await deleteDoc(doc(db, "exercises", ex.id));
                                          }
                                        }}
                                        className="text-rose-400 hover:text-rose-300 transition bg-slate-950 hover:bg-slate-800 border border-slate-800 p-1.5 rounded-lg cursor-pointer flex items-center justify-center"
                                        title={locale === 'pl' ? "Usuń" : "Delete"}
                                      >
                                        <span className="material-symbols-outlined text-xs">delete</span>
                                      </button>
                                    </div>
                                  </div>

                                  {(() => {
                                    const mediaUrl = ex.imageUrl || ex.gif_url || ex.image || "";
                                    const youtubeLink = ex.youtubeUrl || ex.videoUrl || "";
                                    if (!mediaUrl && !youtubeLink) return null;

                                    return (
                                      <div className="my-1.5 flex flex-wrap items-center gap-2" onClick={(e) => e.stopPropagation()}>
                                        {mediaUrl && (
                                          <button
                                            type="button"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              const imgList = Array.isArray(ex.images) && ex.images.length > 0
                                                ? ex.images
                                                : [mediaUrl, ex.gif_url, ex.image, ex.imageUrl2].filter(Boolean);

                                              const uniqueImgs = Array.from(new Set(imgList));

                                              setEnlargedGlobalImage({ 
                                                url: mediaUrl,
                                                imageUrl2: ex.imageUrl2,
                                                images: uniqueImgs,
                                                title: locale === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name) 
                                              });
                                            }}
                                            className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-rose-300 hover:text-white border border-rose-500/30 hover:border-rose-500/60 p-1.5 pr-3 rounded-xl transition shadow-md cursor-pointer group active:scale-95"
                                            title={locale === 'pl' ? "Kliknij, aby powiększyć zdjęcie/GIF" : "Click to enlarge picture/GIF"}
                                          >
                                            <div className="w-8 h-8 rounded-lg overflow-hidden border border-rose-500/40 bg-slate-900 shrink-0 flex items-center justify-center relative">
                                              <span className="material-symbols-outlined text-xs text-rose-400">image</span>
                                              <img 
                                                src={mediaUrl} 
                                                alt={ex.name} 
                                                className="absolute inset-0 w-full h-full object-contain p-0.5 bg-slate-950/80 group-hover:scale-110 transition duration-200" 
                                                onError={(ev) => { ev.target.style.display = 'none'; }}
                                              />
                                            </div>
                                            <span className="text-[11px] font-extrabold flex items-center gap-1.5">
                                              <span className="material-symbols-outlined text-xs text-rose-400 group-hover:scale-110 transition">visibility</span>
                                              <span>{locale === 'pl' ? "Pokaż Zdjęcie / GIF" : "View Media"}</span>
                                            </span>
                                          </button>
                                        )}

                                      {(ex.youtubeUrl || ex.videoUrl) && (
                                        <a
                                          href={ex.youtubeUrl || ex.videoUrl}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          onClick={(e) => e.stopPropagation()}
                                          className="inline-flex items-center gap-1.5 bg-red-950/80 hover:bg-red-900/90 text-red-300 hover:text-white border border-red-500/40 px-3 py-2 rounded-xl text-[11px] font-extrabold transition shadow-md active:scale-95 cursor-pointer"
                                          title={locale === 'pl' ? "Obejrzyj instrukcję wideo w serwisie YouTube" : "Watch YouTube instruction video"}
                                        >
                                          <span className="material-symbols-outlined text-sm text-red-400">play_circle</span>
                                          <span>YouTube</span>
                                        </a>
                                      )}
                                      </div>
                                    );
                                  })()}

                                  <h4 className="font-extrabold text-sm text-white m-0 leading-tight">
                                    {locale === 'pl' ? (ex.namePL || ex.name) : (ex.nameEN || ex.name)}
                                  </h4>
                                  
                                  {(ex.namePL && ex.nameEN && ex.namePL !== ex.nameEN) && (
                                    <span className="text-[10px] text-slate-400 font-bold block italic">
                                      {locale === 'pl' ? `EN: ${ex.nameEN}` : `PL: ${ex.namePL}`}
                                    </span>
                                  )}

                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className="inline-block bg-slate-950 text-slate-300 border border-slate-800 text-[10px] font-extrabold uppercase px-2 py-0.5 rounded-md">
                                      {translateTerm(ex.bodyPart || ex.category || 'General', locale)}
                                    </span>                                  </div>

                                  {(ex.hintsPL || ex.hintsEN || ex.hints) && (
                                    <p className="text-[11px] text-slate-400 italic leading-relaxed m-0">
                                      {locale === 'pl' ? (ex.hintsPL || ex.hints || ex.notes) : (ex.hintsEN || ex.hints || ex.notes)}
                                    </p>
                                  )}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  );
                })()}

              </div>
            )}

            {/* SUB-TAB 2: GLOBAL ROUTINES */}
            {globalSubTab === 'routines' && (
              <div className="space-y-6">
                <InteractiveWorkoutBuilder
                  initialTitle={globalRtTitle}
                  initialTag={globalRtTag}
                  initialExercises={globalRtExercises}
                  isGlobalOnly={true}
                  allExercises={allExercises}
                  lang={locale}
                  onSave={async (routineData) => {
                    const rtId = 'rt_global_' + Date.now();
                    const payload = {
                      id: rtId,
                      docId: rtId,
                      title: routineData.title,
                      tag: routineData.tag,
                      isGlobal: true,
                      assignedTo: "global",
                      assignedClientId: "global",
                      trainerId: "global",
                      exercises: routineData.exercises,
                      createdAt: Date.now()
                    };
                    await setDoc(doc(db, "routines", rtId), payload);
                    setGlobalRtTitle("");
                    setGlobalRtExercises([]);
                    alert(locale === 'pl' ? "🎉 Utworzono nowy globalny szablon!" : "🎉 Created new global routine!");
                  }}
                />

                {/* List of Global Routines */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {allRoutines.filter(r => r.isGlobal || r.assignedTo === 'global' || r.assignedClientId === 'global' || r.trainerId === 'global').map((rt) => (
                    <div key={rt.id} className="bg-slate-900 border border-slate-800 p-5 rounded-2xl space-y-3 shadow-lg flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[9px] font-black uppercase px-2 py-0.5 rounded-md">
                            🌐 GLOBALNY SZABLON
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm(locale === 'pl' ? `Usuń z Globalnej Biblioteki: ${rt.title}?` : `Delete global routine: ${rt.title}?`)) {
                                await deleteDoc(doc(db, "routines", rt.id));
                              }
                            }}
                            className="text-rose-400 hover:text-rose-300 transition bg-transparent border-0 p-1 cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-xs">delete</span>
                          </button>
                        </div>
                        <h4 className="font-extrabold text-base text-white m-0">{rt.title}</h4>
                        <span className="inline-block bg-indigo-500/20 text-indigo-300 border border-indigo-500/30 text-[10px] font-extrabold px-2 py-0.5 rounded-md">
                          {rt.tag || '#FBW'}
                        </span>
                        <div className="text-xs text-slate-400 font-medium pt-1">
                          <strong>Ćwiczenia ({(rt.exercises || []).length}):</strong> {(rt.exercises || []).map(e => e.namePL || e.name).join(', ')}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* SUB-TAB 3: GLOBAL PLANS */}
            {globalSubTab === 'plans' && (
              <div className="space-y-6">
                <InteractivePlanBuilder
                  initialTitle={globalPlanTitle}
                  initialNotes={globalPlanNotes}
                  initialDays={globalPlanRoutines}
                  availableRoutines={allRoutines.filter(r => r.isGlobal === true || (r.assignedTo || '').toLowerCase() === 'global' || (r.assignedClientId || '').toLowerCase() === 'global' || (r.trainerId || '').toLowerCase() === 'global')}
                  isGlobalOnly={true}
                  lang={locale}
                  onSave={async (planData) => {
                    const planId = 'plan_global_' + Date.now();
                    const payload = {
                      id: planId,
                      docId: planId,
                      title: planData.title,
                      notes: planData.notes,
                      durationWeeks: planData.durationWeeks,
                      includeDeload: planData.includeDeload,
                      isGlobal: true,
                      assignedTo: "global",
                      assignedClientId: "global",
                      assignedToName: "Global Library (Standard)",
                      trainerId: "global",
                      routines: planData.days,
                      createdAt: Date.now()
                    };
                    await setDoc(doc(db, "plans", planId), payload);
                    setGlobalPlanTitle("");
                    setGlobalPlanNotes("");
                    alert(locale === 'pl' ? "🎉 Utworzono nowy globalny plan!" : "🎉 Created new global plan!");
                  }}
                />

                {/* List of Global Plans */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {allPlans.filter(p => p.isGlobal || p.assignedTo === 'global' || p.assignedClientId === 'global' || p.trainerId === 'global').map((plan) => (
                    <div key={plan.id} className="bg-slate-900 border border-slate-800 p-5 rounded-2xl space-y-3 shadow-lg flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-2">
                          <span className="bg-rose-500/15 text-rose-300 border border-rose-500/30 text-[9px] font-black uppercase px-2 py-0.5 rounded-md">
                            🌐 GLOBALNY PLAN WIELODNIOWY
                          </span>
                          <button
                            type="button"
                            onClick={async () => {
                              if (window.confirm(locale === 'pl' ? `Usuń z Globalnej Biblioteki: ${plan.title}?` : `Delete global plan: ${plan.title}?`)) {
                                await deleteDoc(doc(db, "plans", plan.id));
                              }
                            }}
                            className="text-rose-400 hover:text-rose-300 transition bg-transparent border-0 p-1 cursor-pointer"
                          >
                            <span className="material-symbols-outlined text-xs">delete</span>
                          </button>
                        </div>
                        <h4 className="font-extrabold text-base text-white m-0">{plan.title}</h4>
                        {plan.notes && (
                          <p className="text-xs text-slate-400 italic m-0 font-medium">"{plan.notes}"</p>
                        )}
                        <div className="text-xs text-slate-300 font-bold pt-1 space-y-1">
                          {(plan.routines || []).map((r, idx) => (
                            <div key={idx} className="bg-slate-950 px-3 py-1.5 rounded-lg border border-slate-800 flex justify-between">
                              <span>Dzień {r.dayNumber}: {r.routineTitle}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Edit Branding Modal Overlay */}
        {editingCoachBranding && (
          <div className="fixed inset-0 bg-slate-950/80 flex items-center justify-center p-4 z-50 animate-fadeIn backdrop-blur-sm">
            <div className="w-full max-w-md bg-slate-900 border border-slate-800 p-6 sm:p-7 rounded-3xl space-y-5 shadow-2xl">
              <div className="border-b border-slate-800 pb-3 flex justify-between items-center">
                <h3 className="text-sm md:text-base font-black text-white uppercase tracking-wider m-0 flex items-center gap-2">
                  <span className="material-symbols-outlined text-base text-rose-400">palette</span>
                  <span>{locale === 'pl' ? `Edycja Brandingu: ${editingCoachBranding.displayName || editingCoachBranding.email}` : `Edit Branding: ${editingCoachBranding.displayName || editingCoachBranding.email}`}</span>
                </h3>
                <button onClick={() => setEditingCoachBranding(null)} className="text-slate-400 hover:text-white bg-transparent border-0 cursor-pointer flex items-center"><span className="material-symbols-outlined text-sm">close</span></button>
              </div>
              
              <div className="flex items-center gap-3 bg-slate-950/60 p-4 rounded-2xl border border-slate-800">
                <input 
                  type="checkbox" 
                  id="editBrandCustom" 
                  checked={brandCustom} 
                  onChange={e => setBrandCustom(e.target.checked)} 
                  className="w-4 h-4 bg-slate-950 border-slate-750 rounded text-indigo-600 cursor-pointer" 
                />
                <label htmlFor="editBrandCustom" className="text-xs font-black text-slate-200 cursor-pointer">
                  {locale === 'pl' ? 'Włącz własny branding dla tego trenera' : 'Enable Custom White-Labeling'}
                </label>
              </div>

              {brandCustom && (
                <div className="space-y-4 bg-slate-950/40 p-4 rounded-2xl border border-slate-850">
                  <div>
                    <label className="text-[11px] font-black text-slate-400 uppercase block mb-1.5">
                      {locale === 'pl' ? 'Logo (Tekst lub URL do obrazka PNG/SVG)' : 'Logo (Text or URL to PNG/SVG Image)'}
                    </label>
                    <input 
                      type="text" 
                      value={brandLogo} 
                      onChange={e => setBrandLogo(e.target.value)} 
                      placeholder={locale === 'pl' ? 'np. Team Piątek 🦅 lub https://mojadomena.pl/logo.png' : 'e.g., Team Piątek 🦅 or https://mydomain.com/logo.png'}
                      className="w-full bg-slate-900 border border-slate-750 rounded-xl px-3.5 py-3 text-xs sm:text-sm font-bold text-white focus:outline-none focus:border-indigo-400 shadow-inner" 
                    />
                  </div>
                  <div>
                    <label className="text-[11px] font-black text-slate-400 uppercase block mb-1.5">
                      {locale === 'pl' ? 'Kolor Przewodni (Hex)' : 'Primary Brand Color'}
                    </label>
                    <div className="flex gap-2.5 items-center">
                      <input 
                        type="color" 
                        value={brandColor} 
                        onChange={e => setBrandColor(e.target.value)} 
                        className="w-12 h-12 bg-transparent border-0 cursor-pointer p-0 rounded-xl" 
                      />
                      <input 
                        type="text" 
                        value={brandColor} 
                        onChange={e => setBrandColor(e.target.value)} 
                        className="flex-1 bg-slate-900 border border-slate-750 rounded-xl px-3.5 py-3 text-xs sm:text-sm font-mono font-extrabold uppercase text-white focus:outline-none focus:border-indigo-400 text-center shadow-inner" 
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-3 border-t border-slate-800">
                <button 
                  onClick={() => setEditingCoachBranding(null)} 
                  className="text-xs sm:text-sm font-extrabold text-slate-400 hover:text-white bg-slate-800 hover:bg-slate-700 px-4 py-2.5 rounded-xl transition cursor-pointer"
                >
                  {t('common.cancel', 'Cancel')}
                </button>
                <button 
                  onClick={async () => {
                    try {
                      const payload = {
                        brandCustom: brandCustom,
                        brandColor: brandCustom ? brandColor : "#6366f1",
                        brandLogo: brandCustom ? brandLogo.trim() : ""
                      };
                      updateDoc(doc(db, "users", editingCoachBranding.id), payload).catch(err => console.warn("Offline note:", err.message));
                      setAllUsers(prev => prev.map(u => u.id === editingCoachBranding.id ? { ...u, ...payload } : u));
                      setEditingCoachBranding(null);
                      alert(locale === 'pl' ? "🎉 Branding trenera został pomyślnie zaktualizowany!" : "🎉 Trainer branding successfully updated!");
                    } catch (err) {
                      console.error("Error saving coach branding:", err);
                    }
                  }} 
                  className="bg-indigo-600 hover:bg-indigo-500 text-white text-xs sm:text-sm font-black px-5 py-2.5 rounded-xl transition shadow-lg shadow-indigo-500/30 cursor-pointer active:scale-95 flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">save</span>
                  <span>{t('common.save', 'Save Changes')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Change Password Dialog Modal Overlay */}
        {passwordModalUser && (
          <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fadeIn">
            <div className="bg-slate-900 border border-slate-750 rounded-3xl p-6 md:p-8 max-w-md w-full shadow-[0_10px_40px_rgba(0,0,0,0.8)] space-y-5">
              <div className="flex items-center justify-between border-b border-slate-800 pb-3.5">
                <div className="flex items-center gap-2.5">
                  <span className="material-symbols-outlined text-2xl text-indigo-400">key</span>
                  <h3 className="text-lg md:text-xl font-black text-white m-0">
                    {locale === 'pl' ? "Zmień Hasło Użytkownika" : "Change User Password"}
                  </h3>
                </div>
                <button 
                  onClick={() => setPasswordModalUser(null)}
                  className="text-slate-400 hover:text-white transition bg-transparent border-0 cursor-pointer p-1 flex items-center justify-center"
                >
                  <span className="material-symbols-outlined text-lg">close</span>
                </button>
              </div>

              <div className="space-y-2 text-xs text-slate-300">
                <p className="m-0 font-medium">
                  {locale === 'pl' 
                    ? `Wprowadź nowe hasło logowania dla: ` 
                    : `Enter a new login password for: `}
                  <strong className="text-white font-extrabold block text-sm mt-1">{passwordModalUser.displayName || passwordModalUser.name} ({passwordModalUser.email})</strong>
                </p>
              </div>

              <div className="space-y-4">
                <div className="relative flex items-center">
                  <input
                    type={showModalPassword ? "text" : "password"}
                    value={newPasswordInput}
                    onChange={(e) => setNewPasswordInput(e.target.value)}
                    placeholder={locale === 'pl' ? "Nowe hasło..." : "New password..."}
                    className="w-full bg-slate-950 border border-slate-700 text-white font-extrabold text-sm rounded-xl pl-4 pr-11 py-3 outline-none focus:border-indigo-400 shadow-inner"
                  />
                  <button
                    type="button"
                    onClick={() => setShowModalPassword(!showModalPassword)}
                    className="absolute right-3 text-slate-400 hover:text-white transition focus:outline-none flex items-center cursor-pointer p-1"
                    title={showModalPassword ? (locale === 'pl' ? "Ukryj hasło" : "Hide password") : (locale === 'pl' ? "Pokaż hasło" : "Show password")}
                  >
                    <span className="material-symbols-outlined text-lg">{showModalPassword ? "visibility_off" : "visibility"}</span>
                  </button>
                </div>

                {/* Real-time Password Strength UI Indicator */}
                <div className="bg-slate-950/60 border border-slate-800/80 rounded-xl p-3 flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mr-1 flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs text-slate-400">shield_lock</span>
                    {locale === 'pl' ? "Wymagania:" : "Requirements:"}
                  </span>
                  <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${newPasswordInput.length >= 8 ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    Min. 8 Chars
                  </span>
                  <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[A-Z]/.test(newPasswordInput) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    A-Z
                  </span>
                  <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[a-z]/.test(newPasswordInput) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    a-z
                  </span>
                  <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[0-9]/.test(newPasswordInput) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    0-9
                  </span>
                  <span className={`text-[10px] font-extrabold px-2.5 py-1 rounded-lg transition-colors ${/[@$!%*?&._-]/.test(newPasswordInput) ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/30' : 'bg-slate-900 text-slate-500 border border-slate-800'}`}>
                    Special Char
                  </span>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2 border-t border-slate-800/80">
                <button
                  type="button"
                  onClick={() => setPasswordModalUser(null)}
                  className="bg-slate-800 hover:bg-slate-700 text-slate-300 font-bold px-5 py-2.5 rounded-xl text-xs transition cursor-pointer"
                >
                  {t('common.cancel', 'Anuluj')}
                </button>
                <button
                  type="button"
                  onClick={handleExecuteChangePassword}
                  className="bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-400 hover:to-indigo-500 text-white font-black px-6 py-2.5 rounded-xl text-xs transition shadow-lg shadow-indigo-500/20 cursor-pointer active:scale-95 flex items-center gap-1.5"
                >
                  <span className="material-symbols-outlined text-sm">save</span>
                  <span>{t('common.save', 'Zapisz')}</span>
                </button>
              </div>
            </div>
          </div>
        )}

      </main>

      {/* GLOBAL MEDIA LIGHTBOX MODAL */}
      <MediaLightboxModal 
        mediaData={enlargedGlobalImage} 
        onClose={() => setEnlargedGlobalImage(null)} 
        lang={locale} 
      />
    </div>
  );
}
